import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import ChartJS from 'chart.js/auto';

import styles from '../../css/admin/Status.module.css';
import { useLanguage } from '../../hooks/LanguageContext';
import useAdminProfile from '../../hooks/useAdminProfile';
import useAxiosAdminFetch from '../../hooks/useAxiosAdminFetch';
import translations from '../admin/translations';
import StatusTabs from './StatusTabs';
// [import 추가 1] SMS 본인확인 모달 재사용. 인라인 스타일이라 별도 CSS 없이 모바일에서 그대로 쓴다.
import SmsVerifyModal from '../../components/SmsVerifyModal';
// [import 추가 2] SmsVerifyModal 이 쓰는 12개 키(authCode/cancel/smsVerify*)가
//  admin/translations.js 에는 전부 없고 data/translation.js 에만 있다(:396-406, :804-814).
//  translations 파일 수정이 금지라 이 파일을 추가로 읽어 SmsVerifyModal 의 t prop 으로만 쓴다.
import webTranslations from '../../data/translation';
import { Config } from '../../common/config';
import { formatGiftSerialDisplay } from '../../utils/formatGiftSerial';
import { isApp } from '../../utils/isApp'; // [APP-FORK]
import { prepareGiftImage } from '../../utils/resizeGiftImage';
import { isSuccessResponse, getErrorMessage } from '../../components/utils';
import useBodyScrollLock from '../../hooks/useBodyScrollLock';
import Swal from 'sweetalert2';
import GiftCouponPicker, { ISSUER_LABEL } from '../../components/GiftCouponPicker';
import { couponLinesOf } from '../../utils/couponSales.util'; // [2026-11-05] 판매현황 쿠폰 사용 표시

// 안내문구의 줄바꿈(\n)을 그대로 보여준다
const preLine = () => {
  const el = Swal.getHtmlContainer();
  if (el) el.style.whiteSpace = 'pre-line';
};
const confirmSwal = async text => {
  const r = await Swal.fire({
    icon: 'question',
    text,
    showCancelButton: true,
    confirmButtonText: '확인',
    cancelButtonText: '취소',
    reverseButtons: true,
    didOpen: preLine,
  });
  return r.isConfirmed;
};

// 웹 기프트 수정(giftManage.js)과 같은 규칙·문구로 입력 중 안내문구를 만든다
const priceInputError = (value, wt, max, isNew) => {
  const n = parseInt(value, 10) || 0;
  // 등록은 1,000,000원 이상, 수정은 1,000,000원 초과를 막는다 (웹 등록/수정 화면과 동일)
  if (max && isNew && n >= max) return wt.giftPriceUnitManage;
  if (max && !isNew && n > max) return wt.giftPriceMaxManage;
  if (n % 100 !== 0) return wt.giftPriceUnit;
  return '';
};

const ymd = d =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// 직원 등록/수정 폼 초기값. staffManage.js:12-20 EMPTY_FORM 과 필드 동일.
const EMPTY_STAFF_FORM = {
  adminId: '',
  password: '',
  passwordConfirm: '',
  adminName: '',
  mobile: '',
};

export default function Status() {
  const { storeNo, tab } = useParams();
  const { isOwner } = useAdminProfile();
  const activeTabParam = tab;
  const activeTab =
    activeTabParam === 'gift'
      ? 'gift'
      : activeTabParam === 'sales'
        ? 'sales'
        : activeTabParam === 'staff'
          ? 'staff'
          : 'home';
  const [giftState, setGiftState] = useState([]);
  const [salesRows, setSalesRows] = useState([]);
  const [giftSaleSummary, setGiftSaleSummary] = useState([]);
  const [salesSummary, setSalesSummary] = useState({ today: 0, week: 0, month: 0 });
  const [salesTrend, setSalesTrend] = useState([]);
  const [giftFilter, setGiftFilter] = useState('ALL');
  const [selectedGift, setSelectedGift] = useState(null);
  const [giftSalesRows, setGiftSalesRows] = useState([]);
  const [giftSalesLoading, setGiftSalesLoading] = useState(false);
  const [salesFilter, setSalesFilter] = useState('all');
  // 정렬 기준: C1 결제일 / C2 사용일 / C3 입금일 (백엔드 salesStatus searchCon 값, 항상 최신순). 기본은 사용일순.
  const [salesSort, setSalesSort] = useState('C2');
  const [editGift, setEditGift] = useState(null);
  const [editForm, setEditForm] = useState({});
  // [2026-10-28] 기프트 등록 · 수정의 바우처 프로모션 선택 · 쿠폰 선택 — 웹(giftContent.js · giftManage.js)과 같은 규칙
  const [availableVouchers, setAvailableVouchers] = useState([]);
  const [vouchersLoaded, setVouchersLoaded] = useState(false);
  const [couponCandidates, setCouponCandidates] = useState([]);
  const [couponsLoaded, setCouponsLoaded] = useState(false);
  const [selectedCouponNos, setSelectedCouponNos] = useState([]);
  // 이미 골라져 있지만 종료 · 중지돼 후보에 없는 쿠폰 (회색 "(종료)" 표시)
  const [endedCoupons, setEndedCoupons] = useState([]);
  // 선택 기록이 없는 기존 허용 기프트 — 후보를 받으면 전체 체크로 연다(웹과 같음)
  const [pendingSelectAllCoupons, setPendingSelectAllCoupons] = useState(false);
  const [couponSelectError, setCouponSelectError] = useState('');
  const [editBusy, setEditBusy] = useState(false);
  const [editError, setEditError] = useState('');
  const [editImageFile, setEditImageFile] = useState(null); // { preview, file }
  const [categoryData, setCategoryData] = useState([]);
  const [staffList, setStaffList] = useState([]);
  // staffTotal: 검색과 무관한 '이 상점의 전체 직원 수'. 등록 버튼 잠금 판정에만 쓴다.
  const [staffTotal, setStaffTotal] = useState(0);
  // staffSearch: 실제 조회에 쓰이는 '확정된' 검색어 — 검색 버튼·엔터로만 갱신된다.
  //  staffSearchInput: 입력창 표시값. 타이핑은 이 값만 바꾸므로 API 가 나가지 않는다.
  const [staffSearch, setStaffSearch] = useState('');
  const [staffSearchInput, setStaffSearchInput] = useState('');
  const [staffLoading, setStaffLoading] = useState(false);
  const [staffModalOpen, setStaffModalOpen] = useState(false);
  const [staffEditingId, setStaffEditingId] = useState('');
  const [staffForm, setStaffForm] = useState(EMPTY_STAFF_FORM);
  const [staffError, setStaffError] = useState('');
  const [staffBusy, setStaffBusy] = useState(false);
  const [staffIdCheckedValue, setStaffIdCheckedValue] = useState('');
  const [staffIdCheckPassed, setStaffIdCheckPassed] = useState(false);
  const [staffSmsModalOpen, setStaffSmsModalOpen] = useState(false);
  const [staffVerifiedMobile, setStaffVerifiedMobile] = useState('');
  // 비밀번호/확인 칸의 표시 토글. 두 칸이 독립으로 동작하도록 상태를 따로 둔다.
  const [showStaffPassword, setShowStaffPassword] = useState(false);
  const [showStaffPasswordConfirm, setShowStaffPasswordConfirm] =
    useState(false);
  // 비밀번호 확인 검사 대상 여부 — 수정 모드에서 비밀번호를 비워두면 변경 의사가 없는
  //  것이므로(placeholder '변경할 때만 입력') 확인란도 검사하지 않는다.
  const staffPasswordTouched = !!staffForm.password;
  const staffPasswordMatched =
    !staffPasswordTouched ||
    staffForm.password === staffForm.passwordConfirm;
  const { fetchData } = useAxiosAdminFetch(); // 로딩의미: api 호출 중
  const { language } = useLanguage(); // 언어 상태 가져오기
  const t = translations[language]; // Translations short-hand
  // SmsVerifyModal 전용 번역 묶음(위 import 주석 참고). 언어 키는 t 와 동일하게 맞춘다.
  const smsT = webTranslations[language] || webTranslations.ko;
  // 기프트 수정·판매시작 안내문구는 PC(giftManage.js)와 같은 문구를 쓴다
  const wt = smsT;
  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);
  const modalBodyRef = useRef(null);
  // 모달(판매현황/기프트 수정)이 열려 있는 동안 배경을 잠가서, 손가락으로 오버레이를
  // 누르고 좌우로 끌 때 iOS 특유의 배경 고무줄(bounce) 스크롤로 화면 전체가 흔들리는 걸 막는다.
  useBodyScrollLock(!!(selectedGift || editGift), modalBodyRef);

  // 직원 모달은 전용 ref 로 따로 잠근다 — modalBodyRef 를 공유하면 두 모달이 동시에
  //  마운트됐을 때 나중에 attach 된 쪽만 allowScrollRef 가 되어 다른 모달의 본문
  //  스크롤이 죽는다. 훅은 lockCount 참조 카운트라(useBodyScrollLock.js:18-21)
  //  중복 호출이 안전하다.
  const staffModalBodyRef = useRef(null);
  useBodyScrollLock(staffModalOpen, staffModalBodyRef);

  // URL storeNo 바꿔치기 방지: 로그인한 관리자 계정의 storeNo와 다르면 안내 후 본인 상점으로 이동
  useEffect(() => {
    const adminId = localStorage.getItem('admin_userid');
    if (!adminId || !storeNo) return;

    (async () => {
      try {
        const detail = await fetchData({
          url: `${process.env.REACT_APP_API_SERVER}detail/${adminId}`,
          method: 'get',
        });
        const myStoreNo = detail?.data?.data?.storeNo;
        if (!myStoreNo) return;
        if (String(myStoreNo) === String(storeNo)) return;

        Swal.fire({
          icon: 'warning',
          title: t.storeMismatch || '상점 불일치',
          html:
            (t.storeMismatchMessage || '다른 상점으로 접속했습니다.').replace(
              '{id}',
              adminId
            ) + `<br/><br/>${t.goToStore || '해당 상점으로 이동'}`,
          confirmButtonText: t.goToStore || '해당 상점으로 이동',
        }).then(() => {
          window.location.replace(`/mobile/giftstatus/${myStoreNo}/sales`);
        });
      } catch (e) {
        // 세션 만료 등은 useAxiosAdminFetch에서 처리
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeNo]);

  const loadGiftData = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        storeNo: String(storeNo),
        historyYn: 'Y',
        pageNo: '1',
        pageSize: '200',
      });
      if (giftFilter !== 'ALL') {
        params.set('state', giftFilter);
      }
      const url = `${process.env.REACT_APP_API_SERVER}gift?${params.toString()}`;
      const response = await fetchData({
        method: 'GET',
        url: url,
      });
      setGiftState(response?.data?.data?.list || []);
    } catch (error) {
      console.error('error:', error);
      setGiftState([]);
    }
  }, [fetchData, storeNo, giftFilter]);

  // giftNo 기준 조회 — 서버가 같은 시리즈(이전·현재 버전)를 묶어서 돌려준다.
  const loadGiftSalesData = useCallback(async (giftNo, sort = 'C2') => {
    setGiftSalesLoading(true);
    try {
      const response = await fetchData({
        method: 'GET',
        url: `${process.env.REACT_APP_API_SERVER}gift/salesStatus/${storeNo}?pageNo=1&pageSize=200&giftNo=${giftNo}&searchCon=${sort}&conOrd=desc`,
      });
      const rows = response?.data?.data?.list?.[0] || [];
      setGiftSalesRows(Array.isArray(rows) ? rows : []);
    } catch (e) {
      console.error('gift sales load error:', e);
      setGiftSalesRows([]);
    } finally {
      setGiftSalesLoading(false);
    }
  }, [fetchData, storeNo]);

  const handleGiftClick = useCallback((gift) => {
    setSelectedGift(gift);
    setGiftSalesRows([]);
    loadGiftSalesData(gift.giftNo, salesSort);
  }, [loadGiftSalesData, salesSort]);

  const handleSalesSort = code => {
    setSalesSort(code);
    loadSalesData(code);
  };

  const loadSalesData = useCallback(async (sort = 'C2') => {
    try {
      const response = await fetchData({
        method: 'GET',
        url: `${process.env.REACT_APP_API_SERVER}gift/salesStatus/${storeNo}?pageNo=1&pageSize=200&searchCon=${sort}&conOrd=desc`,
      });
      const rows = response?.data?.data?.list?.[0] || [];
      setSalesRows(Array.isArray(rows) ? rows : []);
      const giftSummary = response?.data?.data?.list?.[2] || [];
      setGiftSaleSummary(Array.isArray(giftSummary) ? giftSummary : []);
    } catch (e) {
      console.error('sales load error:', e);
      setSalesRows([]);
      setGiftSaleSummary([]);
    }
  }, [fetchData, storeNo]);

  // 오늘/이번주/이번달 판매는 salesRows(client-side price 합산)가 아니라
  // 백엔드가 환불금액까지 반영해 계산해주는 totalSaleAmount를 기간별로 조회해서 쓴다.
  const loadSalesSummary = useCallback(async () => {
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfWeek = new Date(startOfToday);
    startOfWeek.setDate(startOfToday.getDate() - startOfToday.getDay());
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

    const fetchTotal = async startDttm => {
      const res = await fetchData({
        method: 'GET',
        url: `${process.env.REACT_APP_API_SERVER}gift/salesStatus/${storeNo}?pageNo=1&pageSize=1&startDttm=${startDttm}`,
      });
      return Number(res?.data?.data?.list?.[1]?.[0]?.totalSaleAmount || 0);
    };

    try {
      const [today, week, month] = await Promise.all([
        fetchTotal(ymd(startOfToday)),
        fetchTotal(ymd(startOfWeek)),
        fetchTotal(ymd(startOfMonth)),
      ]);
      setSalesSummary({ today, week, month });
    } catch (e) {
      console.error('sales summary load error:', e);
    }
  }, [fetchData, storeNo]);

  // 판매 추이(최근 7일)도 salesRows 클라이언트 합산이 아니라
  // 하루씩 startDttm~endDttm으로 조회한 totalSaleAmount(서버 집계)를 쓴다.
  const loadSalesTrend = useCallback(async () => {
    const days = [];
    for (let i = 6; i >= 0; i -= 1) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      days.push(d);
    }

    const fetchDayTotal = async d => {
      const dateStr = ymd(d);
      const res = await fetchData({
        method: 'GET',
        url: `${process.env.REACT_APP_API_SERVER}gift/salesStatus/${storeNo}?pageNo=1&pageSize=1&startDttm=${dateStr}&endDttm=${dateStr}`,
      });
      return Number(res?.data?.data?.list?.[1]?.[0]?.totalSaleAmount || 0);
    };

    try {
      const amounts = await Promise.all(days.map(fetchDayTotal));
      setSalesTrend(
        days.map((d, idx) => ({
          label: `${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`,
          amount: amounts[idx],
        }))
      );
    } catch (e) {
      console.error('sales trend load error:', e);
    }
  }, [fetchData, storeNo]);

  const loadStaffData = useCallback(async () => {
    setStaffLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('pageNo', '1');
      params.set('pageSize', '200');
      params.set('sortOrd', 'desc');
      if (staffSearch.trim()) {
        params.set('keyword', staffSearch.trim());
      }
      const res = await fetchData({
        method: 'GET',
        url: `${process.env.REACT_APP_API_SERVER}staff?${params.toString()}`,
      });
      const list = res?.data?.data?.list || [];
      setStaffList(list);
      // 검색 중에는 필터된 건수가 오므로 전체 직원 수 판정에 쓰지 않는다.
      if (!staffSearch.trim()) {
        setStaffTotal(res?.data?.data?.total ?? list.length);
      }
    } finally {
      setStaffLoading(false);
    }
  }, [fetchData, staffSearch]);

  // 직원 탭에서만 조회. 조회가 일어나는 시점은 '탭 진입'과 '검색 실행' 두 가지뿐이다.
  //  ★디바운스를 없앤 근거: 예전에는 입력창 값이 곧 staffSearch 라 한 글자마다
  //   loadStaffData 가 새로 만들어지고 이 effect 가 돌았다. 그래서 300ms 타이머와
  //   '첫 조회만 지연 없이' 판정용 staffLoadedOnceRef 가 필요했다. 이제 입력창은
  //   staffSearchInput 이 따로 들고 있어 타이핑으로는 이 effect 가 돌지 않는다
  //   → 도는 두 경우 모두 즉시 조회하면 되고, ref 도 필요 없어 함께 제거했다.
  useEffect(() => {
    if (activeTab !== 'staff') {
      // 탭을 벗어나면 검색어를 비운다(입력창·확정값 둘 다).
      //  ★setStaffSearch('') 는 loadStaffData 를 새로 만들어 이 effect 를 한 번 더
      //   돌리지만, 그 재실행도 이 분기로 들어와 같은 값('')을 세팅하므로 React 가
      //   bail out 해 거기서 멈춘다. API 호출은 이 분기에서 일어나지 않는다.
      setStaffSearch('');
      setStaffSearchInput('');
      return undefined;
    }
    loadStaffData();
    return undefined;
  }, [activeTab, loadStaffData]);

  // 검색 실행 — 검색 버튼과 엔터 키가 공용으로 쓴다.
  //  입력값을 '확정된 검색어'로 올리면 loadStaffData 가 새로 만들어지고 위 effect 가 조회한다.
  //  ★같은 검색어로 다시 누르면 setStaffSearch 가 bail out 해 effect 가 돌지 않는다.
  //   그때만 loadStaffData 를 직접 부른다 — 이 시점의 loadStaffData 는 이미 같은 검색어를
  //   담고 있어 결과가 동일하다(재조회 = 새로고침).
  const runStaffSearch = () => {
    const next = staffSearchInput.trim();
    if (next === staffSearch) {
      loadStaffData();
      return;
    }
    setStaffSearch(next);
  };

  const openStaffCreate = () => {
    setStaffEditingId('');
    setStaffForm(EMPTY_STAFF_FORM);
    setStaffError('');
    setStaffIdCheckedValue('');
    setStaffIdCheckPassed(false);
    setStaffVerifiedMobile('');
    setStaffModalOpen(true);
  };

  // staffManage.js:188-204 onEdit 과 같은 채움 규칙.
  const openStaffEdit = row => {
    setStaffEditingId(row.adminId);
    setStaffForm({
      adminId: row.adminId || '',
      password: '',
      passwordConfirm: '',
      adminName: row.adminName || '',
      mobile: row.mobile || '',
    });
    setStaffError('');
    setStaffIdCheckedValue(row.adminId || '');
    setStaffIdCheckPassed(true);
    // 현재 번호를 '인증된 번호'로 미리 채운다 — 번호를 그대로 두면 재인증 없이 저장된다.
    //  번호를 바꾸면 staffVerifiedMobile 과 달라져 saveStaff 의 인증 검사가 걸린다.
    setStaffVerifiedMobile(row.mobile || '');
    setStaffModalOpen(true);
  };

  // staffManage.js:163-186 onCheckDuplicateId 와 같은 규칙.
  //  ★결과 알림은 window.alert 대신 모달 내부 표시로 바꾼다(2단계 방식 유지).
  const checkStaffDuplicateId = async () => {
    if (staffEditingId) return;
    const adminId = staffForm.adminId.trim();
    if (!adminId) {
      setStaffError('아이디를 입력해 주세요.');
      return;
    }
    const ret = await fetchData({
      method: 'POST',
      url: `${process.env.REACT_APP_API_SERVER}idCheck`,
      data: { adminId },
    });
    const isDuplicated = Boolean(
      ret?.data?.data?.dupResult ?? ret?.data?.dupResult
    );
    if (isDuplicated) {
      setStaffIdCheckPassed(false);
      setStaffIdCheckedValue(adminId);
      setStaffError('이미 사용 중인 아이디입니다.');
      return;
    }
    setStaffIdCheckPassed(true);
    setStaffIdCheckedValue(adminId);
    setStaffError('');
  };

  // SMS 인증 엔드포인트 — 모드별로 다른 API 를 탄다.
  //  · 신규 등록: admin/mail/send-sms · admin/mail/verifySms (SmsMailService.sendSmsAuth).
  //    그 함수는 이미 상점에 소속된 계정을 ConflictException 으로 거부해서
  //    (sms-mail.service.ts:374-379 existingAdmin?.storeNo) 수정 화면에는 쓸 수 없다.
  //  · 수정: admin/profile-auth/sms/send · verify — 수정 화면 전용이고 storeNo 검사가 없다.
  //    AdminService.sendSmsAuth(admin.service.ts:738-757) / verifySms(:691-735) 가 adminId 로
  //    Admin 행을 찾아 처리한다. 가드는 @Roles('admin','manager')(admin.controller.ts:456,473).
  //  ★응답 형태는 두 경로가 같다 — 둘 다 { verifyResult } 를 반환하고 전역 인터셉터
  //   (response.interceptor.ts:43-47)가 { resultCode, resultMessage, data } 로 감싼다
  //   → 아래 읽기 코드(ret?.data?.data?.verifyResult ?? ret?.data?.verifyResult)를 공용한다.
  const staffSmsUrls = staffEditingId
    ? {
        send: `${process.env.REACT_APP_API_SERVER}profile-auth/sms/send`,
        verify: `${process.env.REACT_APP_API_SERVER}profile-auth/sms/verify`,
      }
    : {
        send: `${process.env.REACT_APP_API_SERVER}mail/send-sms`,
        verify: `${process.env.REACT_APP_API_SERVER}mail/verifySms`,
      };

  // staffManage.js:131-145 handleSmsSend 와 동일. 실패는 throw — SmsVerifyModal 이 받아 표시한다.
  //  ★URL 만 staffSmsUrls 로 갈린다. 신규 등록 경로의 요청 형태는 기존과 동일하다.
  const handleStaffSmsSend = async () => {
    const adminId = staffForm.adminId.trim();
    if (!adminId) throw new Error('아이디를 먼저 입력해 주세요.');
    if (!staffForm.mobile.trim()) throw new Error(smsT.smsVerifyNoMobile);
    const ret = await fetchData({
      method: 'POST',
      url: staffSmsUrls.send,
      data: { adminId, mobile: staffForm.mobile.trim() },
    });
    // ret 이 undefined 인 경우(네트워크 오류·타임아웃·세션 만료 401)도 실패로 처리한다.
    if (!ret || ret.status >= 400) {
      throw new Error(ret?.data?.message || 'SMS 발송에 실패했습니다.');
    }
  };

  // staffManage.js:147-160 handleSmsVerify 와 동일. true 면 인증 성공.
  const handleStaffSmsVerify = async code => {
    const ret = await fetchData({
      method: 'POST',
      url: staffSmsUrls.verify,
      data: {
        adminId: staffForm.adminId.trim(),
        mobileAuthNo: Number(code),
      },
    });
    if (!ret || ret.status >= 400) {
      throw new Error(ret?.data?.message || 'SMS 인증 확인에 실패했습니다.');
    }
    const ok = Boolean(ret?.data?.data?.verifyResult ?? ret?.data?.verifyResult);
    if (ok) setStaffVerifiedMobile(staffForm.mobile.trim());
    return ok;
  };

  // staffManage.js:206-213 onDelete 와 같은 규칙. 수정 모드에서만 노출된다.
  const deleteStaff = async () => {
    if (!staffEditingId) return;
    if (!window.confirm('직원 계정을 삭제하시겠습니까?')) return;
    setStaffBusy(true);
    setStaffError('');
    try {
      const ret = await fetchData({
        method: 'DELETE',
        url: `${process.env.REACT_APP_API_SERVER}staff/${staffEditingId}`,
      });
      if (isSuccessResponse(ret)) {
        setStaffModalOpen(false);
        setStaffEditingId('');
        setStaffForm(EMPTY_STAFF_FORM);
        await loadStaffData();
      } else {
        setStaffError(getErrorMessage(ret));
      }
    } catch {
      setStaffError('삭제에 실패했습니다. 다시 시도해 주세요.');
    } finally {
      setStaffBusy(false);
    }
  };

  const saveStaff = async () => {
    const payload = {
      adminId: staffForm.adminId.trim(),
      password: staffForm.password,
      adminName: staffForm.adminName.trim(),
      mobile: staffForm.mobile.trim(),
    };
    // 검증은 staffManage.js:87-99 와 동일. 아이디 중복확인·SMS 인증은 이번 범위 밖이라 뺀다.
    if (!payload.adminName) {
      setStaffError('이름을 입력해 주세요.');
      return;
    }
    // 서버 검증 범위(아이디·이름 80자, 비밀번호 8~64자)를 넘으면 영어 오류가 뜨므로 먼저 막는다.
    if (payload.adminId.length > 80) {
      setStaffError('아이디는 80자 이하로 입력해 주세요.');
      return;
    }
    if (payload.adminName.length > 80) {
      setStaffError('이름은 80자 이하로 입력해 주세요.');
      return;
    }
    if (payload.password && payload.password.length < 8) {
      setStaffError('비밀번호는 8자 이상 입력해 주세요.');
      return;
    }
    if (payload.password && payload.password.length > 64) {
      setStaffError('비밀번호는 64자 이하로 입력해 주세요.');
      return;
    }
    // 비밀번호를 입력했다면(신규는 항상) 확인란과 일치해야 한다.
    if (!staffPasswordMatched) {
      setStaffError('비밀번호가 일치하지 않습니다.');
      return;
    }
    if (!staffEditingId) {
      if (!payload.mobile) {
        setStaffError('휴대폰 전화번호를 입력해 주세요.');
        return;
      }
      if (!payload.adminId) {
        setStaffError('아이디를 입력해 주세요.');
        return;
      }
      if (!staffIdCheckPassed || staffIdCheckedValue !== payload.adminId) {
        setStaffError('아이디 중복확인을 먼저 진행해 주세요.');
        return;
      }
      if (payload.mobile !== staffVerifiedMobile) {
        setStaffError('휴대폰 번호 인증을 완료해 주세요.');
        return;
      }
      if (!payload.password || payload.password.length < 8) {
        setStaffError('비밀번호는 8자 이상 입력해 주세요.');
        return;
      }
    }
    // [수정 모드] 휴대폰 인증 — 신규는 위 블록이 이미 같은 검사를 한다(검사 순서 보존을 위해
    //  기존 블록은 건드리지 않고 여기서 수정 모드만 덧붙인다).
    //  openStaffEdit 이 현재 번호를 staffVerifiedMobile 에 미리 넣어 두므로 번호를 그대로
    //  두면 통과하고, 번호를 바꾸면 새 번호로 인증을 마쳐야(handleStaffSmsVerify) 값이 같아진다.
    if (staffEditingId && payload.mobile !== staffVerifiedMobile) {
      setStaffError('휴대폰 번호 인증을 완료해 주세요.');
      return;
    }

    setStaffBusy(true);
    setStaffError('');
    try {
      let ret;
      if (!staffEditingId) {
        ret = await fetchData({
          method: 'POST',
          url: `${process.env.REACT_APP_API_SERVER}staff`,
          data: payload,
        });
      } else {
        const updatePayload = { ...payload };
        delete updatePayload.adminId;
        if (!updatePayload.password) delete updatePayload.password;
        ret = await fetchData({
          method: 'PUT',
          url: `${process.env.REACT_APP_API_SERVER}staff/${staffEditingId}`,
          data: updatePayload,
        });
      }
      // fetchData는 4xx/5xx여도 throw하지 않고 응답을 그대로 돌려준다(saveGiftEdit 주석과 동일).
      if (isSuccessResponse(ret)) {
        setStaffModalOpen(false);
        setStaffEditingId('');
        setStaffForm(EMPTY_STAFF_FORM);
        setStaffIdCheckedValue('');
        setStaffIdCheckPassed(false);
        setStaffVerifiedMobile('');
        await loadStaffData();
      } else if (ret?.status === 409) {
        setStaffError('이미 사용 중인 아이디입니다.');
      } else {
        setStaffError(getErrorMessage(ret));
      }
    } catch {
      setStaffError('저장에 실패했습니다. 다시 시도해 주세요.');
    } finally {
      setStaffBusy(false);
    }
  };

  useEffect(() => {
    loadGiftData();
    loadSalesData();
    loadSalesSummary();
    loadSalesTrend();
  }, [loadGiftData, loadSalesData, loadSalesSummary, loadSalesTrend]);

  const toDateInput = dateStr => {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr);
      const seoulD = new Date(d.getTime() + (d.getTimezoneOffset() + 9 * 60) * 60000);
      return `${seoulD.getFullYear()}-${String(seoulD.getMonth() + 1).padStart(2, '0')}-${String(seoulD.getDate()).padStart(2, '0')}`;
    } catch { return ''; }
  };

  const isGiftEditable = state => state !== 'SS004' && state !== 'SS005';
  // 연결된 바우처가 기간 종료 등으로 선택 목록에 없음 — 목록을 받은 뒤에만 판단
  const editStaleVoucher =
    vouchersLoaded &&
    !!editForm.discountPromotionNo &&
    !availableVouchers.some(v => String(v.promotionNo) === String(editForm.discountPromotionNo));
  // [2026-10-30] 할인가격은 바우처 프로모션을 골랐을 때만 입력 · 저장 (웹 giftContent.js · giftManage.js 와 같은 규칙)
  //  프로모션 없는 기존 할인은 저장하면 해제된다. 수정 불가 상태는 보기만 하므로 기존 값 그대로 보여준다.
  const editCanDiscount = !!editForm.discountPromotionNo && !editStaleVoucher;
  const editLoadedDiscountNum = parseInt(editForm.discountPrice, 10) || 0;
  const editDiscountNum =
    editCanDiscount || !isGiftEditable(editForm.state) ? editLoadedDiscountNum : 0;
  const editDiscountDropped =
    isGiftEditable(editForm.state) && !editCanDiscount && editLoadedDiscountNum > 0;

  /** [2026-10-28] 이 상점에서 고를 수 있는 VOUCHER · COUPON 목록 (모달 열 때마다 새로) */
  const loadPromotionOptions = useCallback(() => {
    setVouchersLoaded(false);
    setCouponsLoaded(false);
    fetchData({ method: 'GET', url: `${Config.API_SERVER}gift/available-vouchers/${storeNo}` })
      .then(res => {
        const list = res?.data?.data;
        setAvailableVouchers(Array.isArray(list) ? list : []);
        setVouchersLoaded(Array.isArray(list));
      })
      .catch(() => {});
    fetchData({ method: 'GET', url: `${Config.API_SERVER}gift/available-coupons/${storeNo}` })
      .then(res => {
        const list = res?.data?.data;
        setCouponCandidates(Array.isArray(list) ? list : []);
        setCouponsLoaded(Array.isArray(list));
      })
      .catch(() => {});
  }, [fetchData, storeNo]);

  // 선택 기록이 없는 기존 허용 기프트는 지금 쓸 수 있는 쿠폰을 전부 체크해 둔 상태로 연다(웹 giftManage.js 와 같음)
  useEffect(() => {
    if (!pendingSelectAllCoupons || !couponsLoaded) return;
    setSelectedCouponNos(couponCandidates.map(c => Number(c.promotionNo)));
    setPendingSelectAllCoupons(false);
  }, [pendingSelectAllCoupons, couponsLoaded, couponCandidates]);

  const openEditModal = useCallback(async gift => {
    // 채널에서 발행한 기프트(channelInternalYn='Y')는 채널 쪽에서 관리하는
    // 대상이라 상점 관리자 화면에서 수정하면 안 된다 (PC giftList.js와 동일 규칙).
    if (!isGiftEditable(gift.state) || gift.channelInternalYn === 'Y') return;
    setEditError('');
    setEditBusy(false);
    setEditImageFile(null);
    try {
      const [giftRes, codeRes] = await Promise.all([
        fetchData({ method: 'GET', url: `${process.env.REACT_APP_API_SERVER}gift/${gift.giftNo}` }),
        fetchData({ method: 'GET', url: `${Config.API_ADMIN_CODE_DETAIL}?pageSize=20&sortOrd=asc&groupCode=NG02` }),
      ]);
      const d = giftRes?.data?.data || gift;
      if (codeRes?.data?.data?.list) setCategoryData(codeRes.data.data.list);

      const existingImageUrl = d.thumbnailFileUrl && d.thumbnailFileUrl !== process.env.REACT_APP_API_SERVER
        ? d.thumbnailFileUrl : '';
      if (existingImageUrl) setEditImageFile({ preview: existingImageUrl, file: null });

      // 이미 판매 시작된(과거) saleStartDttm을 그대로 보내면 백엔드가
      // "판매시작일은 오늘 또는 이후 날짜만" 검증에 걸려 저장이 실패한다.
      // PC(giftManage.js)와 동일하게 과거면 오늘 날짜로 보정해서 채워둔다.
      const todayStr = toDateInput(new Date());
      const loadedSaleStart = toDateInput(d.saleStartDttm);
      const saleStartDttm =
        loadedSaleStart && loadedSaleStart < todayStr
          ? todayStr
          : loadedSaleStart || todayStr;

      // API는 할인 적용 후 가격을 주므로 할인금액으로 바꿔 채운다(할인은 바우처 프로모션 적용 시에만 저장됨)
      const apiSalePrice = d.discountPrice > 0 ? d.discountPrice : 0;
      const discountAmount = (apiSalePrice > 0 && d.basePrice > 0) ? d.basePrice - apiSalePrice : 0;

      setEditForm({
        giftNo: d.giftNo || gift.giftNo,
        giftName: d.giftName || gift.giftName || '',
        giftDesc: d.giftDesc || '',
        basePrice: String(d.basePrice ?? d.price ?? '0'),
        saleStartDttm,
        saleEndDttm: toDateInput(d.saleEndDttm),
        saleEndUnlimited: !d.saleEndDttm,
        saleEndPre: toDateInput(d.saleEndDttm),
        countIssued: d.countIssued != null ? String(d.countIssued) : '',
        countUnlimited: d.countIssued == null,
        categoryCode: d.categoryCode || 'GC001',
        discountPrice: discountAmount > 0 ? String(discountAmount) : '',
        thumbnailFileUrl: existingImageUrl,
        giftImageNo: d.giftImageNo || '',
        state: d.state || gift.state || 'SS001',
        // [2026-10-28] 바우처 프로모션 · 쿠폰 선택 (웹과 같은 칸)
        discountPromotionNo: d.discountPromotionNo != null ? String(d.discountPromotionNo) : '',
        couponEligibleYn: d.couponEligibleYn || 'Y',
      });
      const loadedCouponNos = (d.couponPromotionNos || []).map(Number);
      setSelectedCouponNos(loadedCouponNos);
      setEndedCoupons((d.couponPromotions || []).filter(c => c.ended));
      setPendingSelectAllCoupons((d.couponEligibleYn || 'Y') === 'Y' && loadedCouponNos.length === 0);
      setCouponSelectError('');
      loadPromotionOptions();
      setEditGift(d.giftNo ? d : gift);
    } catch {
      setEditForm({
        giftNo: gift.giftNo,
        giftName: gift.giftName || '',
        giftDesc: '',
        basePrice: String(gift.price ?? '0'),
        saleStartDttm: '',
        saleEndDttm: '',
        saleEndUnlimited: false,
        saleEndPre: '',
        countIssued: '',
        countUnlimited: false,
        categoryCode: 'GC001',
        discountPrice: '',
        thumbnailFileUrl: '',
        giftImageNo: '',
        state: gift.state || 'SS001',
        discountPromotionNo: gift.discountPromotionNo != null ? String(gift.discountPromotionNo) : '',
        couponEligibleYn: gift.couponEligibleYn || 'Y',
      });
      setSelectedCouponNos([]);
      setEndedCoupons([]);
      setPendingSelectAllCoupons((gift.couponEligibleYn || 'Y') === 'Y');
      setCouponSelectError('');
      loadPromotionOptions();
      setEditGift(gift);
    }
  }, [fetchData, loadPromotionOptions]);

  // 기프트 등록 — 수정 모달을 등록 모드(isNew)로 연다. 초기값은 웹 기프트 등록(giftContent.js)과 같다.
  const openCreateModal = useCallback(async () => {
    setEditError('');
    setEditBusy(false);
    setEditImageFile(null);
    let defaultCategory = '';
    try {
      const codeRes = await fetchData({ method: 'GET', url: `${Config.API_ADMIN_CODE_DETAIL}?pageSize=20&sortOrd=asc&groupCode=NG02` });
      const list = codeRes?.data?.data?.list || [];
      if (list.length) setCategoryData(list);
      // 웹과 같이 5자리 코드 카테고리를 우선 기본값으로
      const preferredFive = list.find(c => c.detailCode && String(c.detailCode).length === 5);
      defaultCategory = preferredFive?.detailCode ?? list[0]?.detailCode ?? '';
    } catch {
      // 카테고리 조회 실패 시 아래 기본값 사용
    }
    setEditForm({
      isNew: true,
      giftNo: '',
      giftName: '',
      giftDesc: '',
      basePrice: '',
      discountPrice: '',
      saleStartDttm: toDateInput(new Date()),
      saleEndDttm: '',
      saleEndUnlimited: false,
      saleEndPre: '',
      countIssued: '',
      countUnlimited: false,
      categoryCode: defaultCategory || 'GC001',
      thumbnailFileUrl: '',
      giftImageNo: '',
      state: '',
      discountPromotionNo: '',
      couponEligibleYn: 'Y',
    });
    setSelectedCouponNos([]);
    setEndedCoupons([]);
    setPendingSelectAllCoupons(false);
    setCouponSelectError('');
    loadPromotionOptions();
    setEditGift({ isNew: true });
  }, [fetchData]); // eslint-disable-line react-hooks/exhaustive-deps

  const prevDayStr = ymd =>
    toDateInput(new Date(new Date(`${ymd}T00:00:00+09:00`).getTime() - 24 * 60 * 60 * 1000));

  // PC giftManage.js openModal('mod')와 같은 규칙으로 저장 전 안내문구를 만든다.
  const buildEditConfirmMessage = useCallback(() => {
    const state = editForm.state;
    const newStart = editForm.saleStartDttm;
    const todayStr = toDateInput(new Date());
    if ((state === 'SS002' || state === 'SS003') && newStart > todayStr) {
      // 판매중·판매중지를 미래 시작으로 고치면 기존 기프트는 새 시작 전날까지 계속 판매된다
      const prevDay = prevDayStr(newStart);
      const origEnd = toDateInput(editGift?.saleEndDttm);
      const oldUntil = origEnd && origEnd < prevDay ? origEnd : prevDay;
      return wt.confirmModifyFuture.replace('{oldUntil}', oldUntil).replace('{newStart}', newStart);
    }
    if (newStart === todayStr) {
      if (state === 'SS001') return wt.confirmModifyTodayReplace;
      const pendingStart = toDateInput(editGift?.pendingSaleStartDttm);
      if (pendingStart) {
        // 판매예정 버전이 있으면 고친 내용은 예약 시작 전날까지만 팔리고, 예약은 그대로 시작된다
        const pendingPrevDay = prevDayStr(pendingStart);
        const newEnd = editForm.saleEndDttm;
        const until = newEnd && newEnd < pendingPrevDay ? newEnd : pendingPrevDay;
        return wt.confirmModifyTodayWithPending.replace('{until}', until).replace('{pendingStart}', pendingStart);
      }
      return wt.confirmModifyToday;
    }
    return wt.confirmModify;
  }, [editForm, editGift, wt]); // eslint-disable-line react-hooks/exhaustive-deps

  // 저장 전 입력 검증 실패 — 웹(giftManage.js)처럼 안내 팝업을 띄우고, 모달 상단에도 남긴다
  const failEdit = msg => {
    setEditError(msg);
    Swal.fire({ icon: 'warning', text: msg, confirmButtonText: '확인', didOpen: preLine });
  };

  // 웹 등록(giftContent.js checkRegGift)·수정(giftManage.js checkModGift)과 같은 순서·문구로 검증한다.
  // 통과하면 '' , 실패하면 안내문구를 돌려준다.
  const validateGiftForm = () => {
    const isNew = !!editForm.isNew;
    const name = editForm.giftName || '';
    const desc = (editForm.giftDesc || '').trim();
    const baseRaw = editForm.basePrice;
    const bpNum = parseInt(baseRaw, 10) || 0;
    const dpNum = editDiscountNum;
    const hasImage = !!(editImageFile?.file || editForm.thumbnailFileUrl || editForm.giftImageNo);
    const endMissing = !editForm.saleEndUnlimited && !editForm.saleEndDttm;
    const countMissing = !editForm.countUnlimited && !((parseInt(editForm.countIssued, 10) || 0) > 0);
    // [2026-10-28] 웹과 같은 프로모션 검증 — 쿠폰 허용인데 고를 쿠폰이 있으면 1개 이상(서버도 400)
    if (
      (editForm.couponEligibleYn || 'Y') === 'Y' &&
      couponsLoaded &&
      couponCandidates.length > 0 &&
      selectedCouponNos.length === 0
    ) {
      setCouponSelectError('적용할 쿠폰을 1개 이상 선택하세요.');
      return '적용할 쿠폰을 1개 이상 선택하세요.';
    }
    // 할인가격이 선택한 바우처 잔여 예산을 넘으면 저장 불가(서버도 400)
    if (editForm.discountPromotionNo && dpNum > 0) {
      const v = availableVouchers.find(x => String(x.promotionNo) === String(editForm.discountPromotionNo));
      const remaining = v ? (v.totalAmount ?? 0) - (v.usedAmount ?? 0) : null;
      if (remaining != null && dpNum > remaining) {
        return `할인가격(${dpNum.toLocaleString()}원)이 선택한 프로모션의 잔여 예산(${remaining.toLocaleString()}원)보다 큽니다.`;
      }
    }
    if (!name.trim()) return wt.giftNameRequired;
    if (name.length > 50) return wt.giftNameMaxLength;
    if (isNew) {
      if (!desc) return wt.giftAdRequired;
      if (desc.length > 100) return wt.giftAdMaxLength;
      if (!hasImage) return wt.giftPhotoRequired;
      if (baseRaw === '' || baseRaw == null) return '정상가격을 입력해 주세요.';
      if (bpNum >= 1000000) return wt.giftPriceMin;
      if (bpNum !== 0 && bpNum % 100 !== 0) return wt.giftPriceUnit;
      if (dpNum % 100 !== 0) return wt.giftPriceUnit;
      if (dpNum > 0 && dpNum >= bpNum) return '할인가격은 정상가격보다 작아야 합니다.';
      if (endMissing) return wt.saleEndRequired;
      if (countMissing) return wt.countIssueRequired;
      return '';
    }
    if (baseRaw === '' || baseRaw == null) return '정상가격을 입력해 주세요.';
    if (bpNum > 1000000) return wt.giftPriceMax;
    if (bpNum !== 0 && bpNum % 100 !== 0) return wt.giftPriceUnit;
    if (endMissing) return wt.saleEndRequired;
    if (countMissing) return wt.countIssueRequired;
    if (dpNum % 100 !== 0) return wt.giftPriceUnit;
    if (dpNum > 0 && dpNum >= bpNum) return '할인가격은 정상가격보다 작아야 합니다.';
    if (desc.length > 100) return wt.giftAdMaxLength;
    if (!hasImage) return wt.giftPhotoRequired;
    const todayStr = toDateInput(new Date());
    if (!editForm.saleStartDttm || editForm.saleStartDttm < todayStr) return wt.giftSaleStartDateInvalid;
    return '';
  };

  // 기프트 등록 — 웹 giftContent.js regGift와 같은 값으로 POST /gift
  const createGift = async () => {
    const bpNum = parseInt(editForm.basePrice, 10) || 0;
    const dpNum = editDiscountNum;
    const todayStr = toDateInput(new Date());
    // 웹과 같이 시작일이 오늘이면 등록 즉시 판매가 시작된다는 걸 알리고 한 번 더 묻는다
    if (editForm.saleStartDttm === todayStr && !(await confirmSwal(wt.confirmRegisterToday))) return;
    setEditBusy(true);
    setEditError('');
    try {
      const fd = new FormData();
      fd.append('giftName', editForm.giftName);
      fd.append('categoryCode', editForm.categoryCode);
      const desc = editForm.giftDesc || '';
      if (desc !== '') fd.append('giftDesc', desc);
      fd.append('basePrice', bpNum);
      if (dpNum > 0) fd.append('discountPrice', bpNum - dpNum);
      fd.append('saleStartDttm', editForm.saleStartDttm);
      if (!editForm.saleEndUnlimited && editForm.saleEndDttm) fd.append('saleEndDttm', editForm.saleEndDttm);
      // 유효기간은 3개월 고정
      fd.append('validity', '3M');
      if (!editForm.countUnlimited) fd.append('countIssued', parseInt(editForm.countIssued, 10));
      fd.append('transferTime', '1M');
      // [2026-10-28] 바우처 · 쿠폰 — 웹 giftContent.js regGift 와 같은 규칙
      fd.append('voucherUseYn', editForm.discountPromotionNo ? 'Y' : 'N');
      if (editForm.discountPromotionNo) fd.append('discountPromotionNo', editForm.discountPromotionNo);
      fd.append('couponEligibleYn', editForm.couponEligibleYn || 'Y');
      // 고를 쿠폰이 없거나 목록을 못 받았으면 안 보낸다 — 서버가 선택 없음(=상점 범위 쿠폰 전부)으로 저장
      if ((editForm.couponEligibleYn || 'Y') === 'Y' && couponsLoaded && couponCandidates.length > 0) {
        fd.append('couponPromotionNos', selectedCouponNos.join(','));
      }
      if (editForm.saleStartDttm === todayStr) fd.append('state', 'SS002');
      if (editImageFile?.file) fd.append('file', editImageFile.file);
      const ret = await fetchData({ url: `${process.env.REACT_APP_API_SERVER}gift`, method: 'post', data: fd });
      if (isSuccessResponse(ret)) {
        setEditGift(null);
        setEditImageFile(null);
        await loadGiftData();
        Swal.fire({ icon: 'success', title: '등록 완료', text: '기프트가 등록되었습니다.', confirmButtonText: '확인' });
      } else {
        const msg = getErrorMessage(ret);
        setEditError(msg);
        Swal.fire({ icon: 'error', title: '등록 실패', text: msg, confirmButtonText: '확인', didOpen: preLine });
      }
    } catch {
      const msg = '등록에 실패했습니다. 다시 시도해 주세요.';
      setEditError(msg);
      Swal.fire({ icon: 'error', title: '등록 실패', text: msg, confirmButtonText: '확인' });
    } finally {
      setEditBusy(false);
    }
  };

  // 판매취소 — 웹 giftManage.js delGift와 같이 확인 후 DELETE /gift/:giftNo
  const cancelGiftSale = async () => {
    if (!(await confirmSwal(wt.confirmCancelSale))) return;
    setEditBusy(true);
    setEditError('');
    try {
      const ret = await fetchData({ url: `${process.env.REACT_APP_API_SERVER}gift/${editForm.giftNo}`, method: 'delete' });
      if (isSuccessResponse(ret)) {
        setEditGift(null);
        setEditImageFile(null);
        await loadGiftData();
        Swal.fire({ icon: 'success', title: '판매취소 완료', text: '기프트가 판매취소되었습니다.', confirmButtonText: '확인' });
      } else {
        const msg = getErrorMessage(ret);
        setEditError(msg);
        Swal.fire({ icon: 'error', title: '판매취소 실패', text: msg, confirmButtonText: '확인', didOpen: preLine });
      }
    } catch {
      const msg = '판매취소에 실패했습니다. 다시 시도해 주세요.';
      setEditError(msg);
      Swal.fire({ icon: 'error', title: '판매취소 실패', text: msg, confirmButtonText: '확인' });
    } finally {
      setEditBusy(false);
    }
  };

  const saveGiftEdit = useCallback(async () => {
    const invalid = validateGiftForm();
    if (invalid) { failEdit(invalid); return; }
    if (editForm.isNew) { await createGift(); return; }
    const bpNum = parseInt(editForm.basePrice, 10) || 0;
    const dpNum = editDiscountNum; // 프로모션 없으면 0 — 할인 없이 저장
    // PC(giftManage.js)와 같은 저장 전 안내 — 오늘 시작이면 즉시 판매·기존 판매취소, 미래 시작이면 기존 판매기간을 알린다.
    if (!(await confirmSwal(buildEditConfirmMessage()))) return;
    setEditBusy(true);
    setEditError('');
    try {
      const fd = new FormData();
      fd.append('giftNo', editForm.giftNo);
      fd.append('giftName', editForm.giftName.trim());
      fd.append('categoryCode', editForm.categoryCode || 'GC001');
      // 웹(giftManage.js)과 같이 비운 경우에도 빈 값으로 보내 새 버전에 기존 문구가 남지 않게 한다
      fd.append('giftDesc', (editForm.giftDesc || '').trim());
      fd.append('basePrice', bpNum);
      if (dpNum > 0) fd.append('discountPrice', bpNum - dpNum);
      fd.append('saleStartDttm', editForm.saleStartDttm);
      if (!editForm.saleEndUnlimited && editForm.saleEndDttm) fd.append('saleEndDttm', editForm.saleEndDttm);
      // 유효기간은 3개월 고정(PC giftManage.js modGift와 동일 규칙)
      fd.append('validity', '3M');
      if (!editForm.countUnlimited) fd.append('countIssued', parseInt(editForm.countIssued, 10));
      fd.append('transferTime', '1M');
      // [2026-10-28] 바우처 · 쿠폰 — 웹 giftManage.js modGift 와 같은 규칙(전엔 바우처 N 고정이라 앱에서 수정하면 연결이 풀렸다)
      //  연결된 바우처가 기간 종료 등으로 선택 목록에 없으면 해제해 저장(그대로 보내면 서버 400)
      const voucherToSave = editStaleVoucher ? '' : editForm.discountPromotionNo;
      fd.append('voucherUseYn', voucherToSave ? 'Y' : 'N');
      if (voucherToSave) fd.append('discountPromotionNo', voucherToSave);
      fd.append('couponEligibleYn', editForm.couponEligibleYn || 'Y');
      // 고른 쿠폰이 없고 고를 쿠폰도 없거나 목록을 못 받았으면 안 보낸다 — 서버가 이전 버전 선택을 이어받음
      if (
        (editForm.couponEligibleYn || 'Y') === 'Y' &&
        couponsLoaded &&
        (couponCandidates.length > 0 || selectedCouponNos.length > 0)
      ) {
        fd.append('couponPromotionNos', selectedCouponNos.join(','));
      }
      if (editImageFile?.file) {
        fd.append('file', editImageFile.file);
      } else if (editForm.giftImageNo) {
        fd.append('giftImageNo', editForm.giftImageNo);
      }
      const ret = await fetchData({ url: `${process.env.REACT_APP_API_SERVER}gift`, method: 'put', data: fd });
      // useAxiosAdminFetch의 fetchData는 4xx/5xx여도 throw하지 않고 응답을 그대로
      // 반환하므로, 여기서 상태코드를 직접 확인하지 않으면 저장이 실패해도 모달이
      // 그냥 닫혀서 "수정이 됐는지 안 됐는지 알 수 없는" 문제가 생긴다.
      if (isSuccessResponse(ret)) {
        setEditGift(null);
        setEditImageFile(null);
        await loadGiftData();
        Swal.fire({
          icon: 'success',
          title: '저장 완료',
          text: '기프트 정보가 수정되었습니다.',
          confirmButtonText: '확인',
        });
      } else {
        const msg = getErrorMessage(ret);
        setEditError(msg);
        Swal.fire({
          icon: 'error',
          title: '저장 실패',
          text: msg,
          confirmButtonText: '확인',
          didOpen: preLine,
        });
      }
    } catch {
      const msg = '저장에 실패했습니다. 다시 시도해 주세요.';
      setEditError(msg);
      Swal.fire({
        icon: 'error',
        title: '저장 실패',
        text: msg,
        confirmButtonText: '확인',
      });
    } finally {
      setEditBusy(false);
    }
  }, [editForm, editImageFile, fetchData, loadGiftData, buildEditConfirmMessage, wt, storeNo, availableVouchers, vouchersLoaded, couponCandidates, couponsLoaded, selectedCouponNos]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleGiftStateChange = useCallback(async (giftNo, currentState) => {
    setEditError('');
    const confirmMsg =
      currentState === 'SS001'
        ? wt.confirmSaleStartReplace
        : currentState === 'SS003'
          ? wt.confirmResume
          : wt.confirmPause;
    if (!(await confirmSwal(confirmMsg))) return;
    try {
      const ret =
        currentState === 'SS001'
          ? await fetchData({ url: `${process.env.REACT_APP_API_SERVER}gift/startSale/${giftNo}`, method: 'patch' })
          : await fetchData({ url: `${process.env.REACT_APP_API_SERVER}gift/toggle/${giftNo}`, method: 'patch' });
      // fetchData는 4xx/5xx여도 throw하지 않으므로 직접 확인해야 실패(예: 이미 판매시작됨)를 알릴 수 있다.
      if (!isSuccessResponse(ret)) {
        const msg = getErrorMessage(ret);
        setEditError(msg);
        Swal.fire({ icon: 'error', title: '상태 변경 실패', text: msg, confirmButtonText: '확인', didOpen: preLine });
        await loadGiftData();
        return;
      }
      const nextState = currentState === 'SS002' ? 'SS003' : 'SS002';
      setEditForm(f => ({ ...f, state: nextState }));
      await loadGiftData();
    } catch {
      setEditError('상태 변경에 실패했습니다.');
    }
  }, [fetchData, loadGiftData, wt]);

  const formatMoney = v =>
    `${Number(v || 0).toLocaleString()}${t.money || '원'}`;
  const formatDate = dateStr => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return '-';
    return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
  };

  const formatDateTime = dateStr => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return '-';
    const p = n => String(n).padStart(2, '0');
    return `${formatDate(dateStr)} ${p(d.getHours())}:${p(d.getMinutes())}`;
  };

  const mapGiftStateLabel = state => {
    if (state === 'SS001') return t.stateDesc?.판매예정 || '판매예정';
    if (state === 'SS002') return t.stateDesc?.판매시작 || '판매시작';
    if (state === 'SS003') return t.stateDesc?.판매중지 || '판매중지';
    if (state === 'SS004') return t.stateDesc?.판매종료 || '판매종료';
    if (state === 'SS005') return t.stateDesc?.판매취소 || '판매취소';
    return t.stateDesc?.판매등록 || '판매예정';
  };

  const getGiftBadgeClass = state => {
    if (state === 'SS001') return styles.badgeSS001;
    if (state === 'SS002') return styles.badgeSS002;
    if (state === 'SS003') return styles.badgeSS003;
    if (state === 'SS004') return styles.badgeSS004;
    if (state === 'SS005') return styles.badgeSS005;
    return styles.badgeSS001;
  };

  const shortenGiftName = name => {
    const safe = String(name || '');
    return safe.length > 7 ? `${safe.slice(0, 7)}..` : safe;
  };

  const mapUsageLabel = (usageStateDesc, usageState) => {
    if (usageStateDesc) return usageStateDesc;
    if (usageState === 'GS002') return '사용완료';
    if (usageState === 'GS001') return '미사용';
    if (usageState === 'GS000') return '미수신';
    return '-';
  };

  // 이름이 있으면 "이름 (이메일)", 없으면 이메일만(수신 전 등 회원 연결이 없는 경우)
  const formatPerson = (name, email) => {
    const n = String(name ?? '').trim();
    const e = String(email ?? '').trim();
    if (n && e) return `${n} (${e})`;
    return n || e || '-';
  };

  const getPayMethodBadgeClass = payMethodDesc => {
    const key = String(payMethodDesc || '');
    if (key === '토스페이') return styles.paymentMethodBadgeToss;
    if (key === '카카오페이') return styles.paymentMethodBadgeKakao;
    if (key === '네이버페이') return styles.paymentMethodBadgeNaver;
    if (key === '페이코') return styles.paymentMethodBadgePayco;
    if (key === '신용카드') return styles.paymentMethodBadgeCard;
    return styles.paymentMethodBadgeDefault;
  };

  const getUsageBadgeClass = usageState => {
    if (usageState === 'GS002') return styles.usageBadgeDone;
    if (usageState === 'GS001') return styles.usageBadgeUnused;
    if (usageState === 'GS000') return styles.usageBadgeUnreceived;
    return styles.usageBadgeDefault;
  };

  // 정산 집계 상태 — payoutYn === 'Y' 이상(정산집계완료/상점지급완료/환불·회수)이면
  // 상점주 환불 불가(백엔드 refund.service.ts 가드와 동일 기준). 배지로만 미리 안내.
  const getSettlementBadgeClass = settlementStateDesc => {
    if (settlementStateDesc === '환불/회수') return styles.depositBadgeRefund;
    if (settlementStateDesc === '정산집계완료' || settlementStateDesc === '상점지급완료') {
      return styles.depositBadgeDone;
    }
    if (settlementStateDesc === '정산대기') return styles.depositBadgePending;
    return styles.depositBadgeDefault;
  };

  const isOwnerRefundApplicant = row =>
    String(row?.refundApplicantType || '').toLowerCase() === 'owner' ||
    row?.refundApplicantTypeCode === 'AT003';

  // 상점주 쪽 액션(환불요청/사용취소)은 실제로 상점에 돈이 걸리기 전(payoutYn='Y' 되기 전)
  // 까지만 허용한다 — 백엔드 createAsOwner() 가드와 동일 기준. '정산대기'(settlementYn='Y',
  // 토스 정산 동기화만 된 상태)는 토스 쪽 기록만 생겼을 뿐 상점에 지급된 돈이 없어서 안전하게
  // 허용해도 된다(월 1회 배치인 정산집계 전까지는 흔히 오래 머무는 상태라, 여기까지 막으면
  // 사용취소 같은 일상적인 작업이 한 달 가까이 막힐 수 있다). '정산집계완료' 이후부터 막는다 —
  // 화이트리스트 방식이라 새 라벨이 추가돼도 기본은 차단(fail-closed)이다.
  const OWNER_ACTION_ALLOWED_SETTLEMENT_STATES = ['입금대기', '정산대기'];

  // 환불 요청(PS021)/실패(PS023)/완료(PS024) 건은 사용취소 불가 — 백엔드 user-gift.service.ts updateStatus 와 동일 기준.
  const canCancelUsage = row =>
    !['PS021', 'PS023', 'PS024'].includes(row?.refundState) &&
    OWNER_ACTION_ALLOWED_SETTLEMENT_STATES.includes(row?.settlementStateDesc);

  const canRequestOwnerRefund = row => {
    if (!row?.purchaseNo || !row?.orderId || !row?.paymentKey) return false;
    if (row.refundState === 'PS024') return false;
    if (row.refundState === 'PS021') return false;
    if (!OWNER_ACTION_ALLOWED_SETTLEMENT_STATES.includes(row?.settlementStateDesc)) return false;
    if (!row.refundState || row.refundState === 'PS022') return true;
    return false;
  };

  const getOwnerRefundStatusNote = row => {
    if (row.refundState === 'PS024') {
      return { text: t.ownerRefundCompleted || '환불 완료', className: styles.refundStatusNoteDone };
    }
    if (row.refundState === 'PS021' && isOwnerRefundApplicant(row)) {
      return { text: t.ownerRefundPending || '환불 요청됨 (처리 대기)', className: styles.refundStatusNotePending };
    }
    if (row.refundState === 'PS023') {
      return { text: t.ownerRefundFailedStatus || '환불 실패', className: styles.refundStatusNoteFailed };
    }
    if (row.refundState === 'PS021') {
      return { text: row.refundStateDesc || '환불 진행중', className: styles.refundStatusNotePending };
    }
    return null;
  };

  const patchSalesRowAfterOwnerRefund = (prev, rowIndex) =>
    prev.map((r, i) =>
      i === rowIndex
        ? {
            ...r,
            refundState: 'PS021',
            refundStateDesc: '환불요청',
            refundApplicantType: 'owner',
            refundApplicantTypeCode: 'AT003',
          }
        : r
    );

  const handleOwnerRefundRequest = useCallback(
    async (row, rowIndex, listKind) => {
      if (!row?.purchaseNo || !row?.orderId || !row?.paymentKey) {
        Swal.fire({
          icon: 'warning',
          title: t.confirm || '확인',
          text: t.ownerRefundMissingPayment || '결제 정보가 없어 환불 요청할 수 없습니다.',
          confirmButtonText: t.confirm || '확인',
        });
        return;
      }

      const result = await Swal.fire({
        icon: 'question',
        title: t.ownerRefundRequest || '상점주 환불 요청',
        text: t.ownerRefundConfirm || '실증 테스트 건에 대해 100% 환불(결제취소)을 요청합니다.',
        showCancelButton: true,
        confirmButtonText: t.confirm || '확인',
        cancelButtonText: '취소',
      });
      if (!result.isConfirmed) return;

      try {
        const response = await fetchData({
          method: 'POST',
          url: `${process.env.REACT_APP_API_SERVER}refund/owner`,
          data: {
            purchaseNo: row.purchaseNo,
            orderId: row.orderId,
            paymentKey: row.paymentKey,
            storeNo: Number(storeNo),
          },
        });
        const ok =
          response?.status >= 200 &&
          response?.status < 300 &&
          (response?.data?.resultCode === 200 || !response?.data?.resultCode);
        if (ok) {
          if (listKind === 'all') {
            setSalesRows(prev => patchSalesRowAfterOwnerRefund(prev, rowIndex));
          } else {
            setGiftSalesRows(prev => patchSalesRowAfterOwnerRefund(prev, rowIndex));
          }
          await Swal.fire({
            icon: 'success',
            title: t.confirm || '확인',
            text: t.ownerRefundSuccess || '환불 요청이 접수되었습니다.',
            confirmButtonText: t.confirm || '확인',
          });
        } else {
          Swal.fire({
            icon: 'error',
            title: t.confirm || '확인',
            text:
              response?.data?.resultMessage ||
              response?.data?.message ||
              t.ownerRefundFailed ||
              '환불 요청에 실패했습니다.',
            confirmButtonText: t.confirm || '확인',
          });
        }
      } catch (err) {
        const msg =
          err?.response?.data?.resultMessage ||
          err?.response?.data?.message ||
          t.ownerRefundFailed ||
          '환불 요청에 실패했습니다.';
        Swal.fire({
          icon: 'error',
          title: t.confirm || '확인',
          text: msg,
          confirmButtonText: t.confirm || '확인',
        });
      }
    },
    [fetchData, storeNo, t]
  );

  const renderOwnerRefundSection = (row, rowIndex, listKind) => {
    const statusNote = getOwnerRefundStatusNote(row);
    if (canRequestOwnerRefund(row)) {
      return (
        <div style={{ marginTop: 8 }}>
          <button
            type="button"
            className={styles.refundRequestBtn}
            onClick={() => handleOwnerRefundRequest(row, rowIndex, listKind)}
          >
            {t.ownerRefundRequest || '상점주 환불 요청'}
          </button>
        </div>
      );
    }
    if (statusNote) {
      return (
        <div className={`${styles.refundStatusNote} ${statusNote.className}`}>
          {statusNote.text}
        </div>
      );
    }
    return null;
  };

  const topGiftSales = useMemo(() => {
    // giftSaleSummary(list[2])엔 state가 없어서, 현재 판매중(SS002)인 기프트만
    // giftState(기프트 목록 API)와 대조해서 걸러낸다.
    const onSaleGiftNos = new Set(
      giftState.filter(g => g.state === 'SS002').map(g => g.giftNo)
    );
    return giftSaleSummary
      .filter(g => onSaleGiftNos.has(g.giftNo))
      .map(g => ({
        giftNo: g.giftNo,
        giftId: g.giftId,
        giftName: g.giftName,
        version: g.version,
        name: g.giftName,
        amount: Number(g.totalSaleAmount || 0),
      }))
      .sort((a, b) => b.amount - a.amount)
      .slice(0, 4);
  }, [giftSaleSummary, giftState]);

  useEffect(() => {
    if (activeTab !== 'home') {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
        chartInstanceRef.current = null;
      }
      return;
    }
    if (!chartRef.current) return;
    const labels = salesTrend.map(t => t.label);
    const data = salesTrend.map(t => t.amount);
    const ctx = chartRef.current.getContext('2d');
    const gradient = ctx.createLinearGradient(0, 0, 0, 180);
    gradient.addColorStop(0, 'rgba(37, 99, 235, 0.2)');
    gradient.addColorStop(1, 'rgba(37, 99, 235, 0)');

    if (chartInstanceRef.current) {
      chartInstanceRef.current.destroy();
    }
    chartInstanceRef.current = new ChartJS(ctx, {
      type: 'line',
      data: {
        labels,
        datasets: [
          {
            data,
            borderColor: '#2563eb',
            backgroundColor: gradient,
            borderWidth: 2,
            pointBackgroundColor: '#ffffff',
            pointBorderColor: '#2563eb',
            pointBorderWidth: 2,
            pointRadius: 4,
            fill: true,
            tension: 0.4,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: {
          y: { beginAtZero: true, ticks: { display: false } },
        },
      },
    });
  }, [salesTrend, activeTab]);

  const handleUsageCancel = useCallback(
    async (userGiftNo, rowIndex) => {
      const result = await Swal.fire({
        icon: 'question',
        title: '사용취소',
        text: '사용완료 상태를 사용전으로 되돌리시겠습니까?',
        showCancelButton: true,
        confirmButtonText: '확인',
        cancelButtonText: '취소',
      });
      if (!result.isConfirmed) return;
      try {
        const response = await fetchData({
          method: 'PATCH',
          url: `${process.env.REACT_APP_API_SERVER}user-gift/${userGiftNo}`,
        });
        const ok =
          response?.status >= 200 &&
          response?.status < 300 &&
          (response?.data?.resultCode === 200 || !response?.data?.resultCode);
        if (ok) {
          setSalesRows(prev =>
            prev.map((r, i) =>
              i === rowIndex
                ? { ...r, usageState: 'GS001', usageStateDesc: '미사용' }
                : r
            )
          );
          await Swal.fire({
            icon: 'success',
            title: '완료',
            text: '사용취소가 완료되었습니다.',
            confirmButtonText: '확인',
          });
        } else {
          Swal.fire({
            icon: 'error',
            title: '오류',
            text: '사용취소에 실패했습니다.',
            confirmButtonText: '확인',
          });
        }
      } catch {
        Swal.fire({
          icon: 'error',
          title: '오류',
          text: '사용취소에 실패했습니다.',
          confirmButtonText: '확인',
        });
      }
    },
    [fetchData]
  );

  const handleGiftSalesUsageCancel = useCallback(
    async (userGiftNo, rowIndex) => {
      const result = await Swal.fire({
        icon: 'question',
        title: '사용취소',
        text: '사용완료 상태를 사용전으로 되돌리시겠습니까?',
        showCancelButton: true,
        confirmButtonText: '확인',
        cancelButtonText: '취소',
      });
      if (!result.isConfirmed) return;
      try {
        const response = await fetchData({
          method: 'PATCH',
          url: `${process.env.REACT_APP_API_SERVER}user-gift/${userGiftNo}`,
        });
        const ok =
          response?.status >= 200 &&
          response?.status < 300 &&
          (response?.data?.resultCode === 200 || !response?.data?.resultCode);
        if (ok) {
          setGiftSalesRows(prev =>
            prev.map((r, i) =>
              i === rowIndex
                ? { ...r, usageState: 'GS001', usageStateDesc: '미사용' }
                : r
            )
          );
          await Swal.fire({
            icon: 'success',
            title: '완료',
            text: '사용취소가 완료되었습니다.',
            confirmButtonText: '확인',
          });
        } else {
          Swal.fire({ icon: 'error', title: '오류', text: '사용취소에 실패했습니다.', confirmButtonText: '확인' });
        }
      } catch {
        Swal.fire({ icon: 'error', title: '오류', text: '사용취소에 실패했습니다.', confirmButtonText: '확인' });
      }
    },
    [fetchData]
  );

  return (
    <div
      className={isApp() ? `${styles.page} ${styles.pageApp}` : styles.page}
    >
      <StatusTabs storeNo={storeNo} activeTab={activeTab} showSettlement={isOwner} />

      {/* 홈: 판매 요약 */}
      {activeTab === 'home' && (
        <section className={styles.section}>
          <div className={styles.salesGrid}>
            <div className={styles.salesCard}>
              <div className={styles.salesLabel}>오늘 판매</div>
              <div className={`${styles.salesValue} ${styles.primary}`}>
                {formatMoney(salesSummary.today)}
              </div>
            </div>
            <div className={styles.salesCard}>
              <div className={styles.salesLabel}>이번주 판매</div>
              <div className={styles.salesValue}>
                {formatMoney(salesSummary.week)}
              </div>
            </div>
            <div className={styles.salesCard}>
              <div className={styles.salesLabel}>이번달 판매</div>
              <div className={styles.salesValue}>
                {formatMoney(salesSummary.month)}
              </div>
            </div>
          </div>
          <div className={styles.card}>
            <div className={styles.sectionTitle}>판매 추이</div>
            <div className={styles.chartArea}>
              <canvas ref={chartRef} />
            </div>
          </div>
          <div className={styles.card}>
            <div className={styles.sectionTitle}>기프트 별 판매 현황</div>
            {topGiftSales.map((g, idx) => {
              const max = Math.max(1, ...topGiftSales.map(x => x.amount));
              const width = `${Math.max(5, Math.round((g.amount / max) * 100))}%`;
              return (
                <div key={g.giftNo} className={styles.barItem}>
                  <div className={styles.barInfo}>
                    <span className={styles.barName}>
                      {formatGiftSerialDisplay(g)}
                    </span>
                    <span className={styles.barValue}>
                      {formatMoney(g.amount)}
                    </span>
                  </div>
                  <div className={styles.barBg}>
                    <div
                      className={`${styles.barFill} ${idx === 0 ? styles.rank1 : idx === 1 ? styles.rank2 : idx === 2 ? styles.rank3 : ''}`}
                      style={{ width }}
                    />
                  </div>
                </div>
              );
            })}
            {topGiftSales.length === 0 && (
              <div className={styles.empty}>데이터가 없습니다</div>
            )}
          </div>
        </section>
      )}

      {/* 기프트 관리: 수정 + 판매현황 모달 */}
      {activeTab === 'gift' && (
        <section className={styles.section}>
          <button type="button" className={styles.giftCreateBtn} onClick={openCreateModal}>
            + {wt.registerGift}
          </button>
          <div className={styles.filterTabs}>
            {[
              { key: 'ALL', label: '전체' },
              { key: 'SS001', label: mapGiftStateLabel('SS001') },
              { key: 'SS002', label: mapGiftStateLabel('SS002') },
              { key: 'SS003', label: mapGiftStateLabel('SS003') },
              { key: 'SS004', label: mapGiftStateLabel('SS004') },
              { key: 'SS005', label: mapGiftStateLabel('SS005') },
            ].map(({ key, label }) => (
              <button
                key={key}
                type="button"
                className={`${styles.filterBtn} ${giftFilter === key ? styles.filterActive : ''}`}
                onClick={() => setGiftFilter(key)}
              >
                {label}
              </button>
            ))}
          </div>
          <div className={styles.card}>
            {giftState.map(gift => (
              <div className={styles.giftItem} key={gift.giftNo}>
                <img
                  className={styles.giftImg}
                  src={gift.thumbnailFileUrl || ''}
                  alt=""
                />
                <div className={styles.giftContent}>
                  <div className={styles.giftHeader}>
                    <span className={styles.giftName}>
                      {formatGiftSerialDisplay(gift)}
                    </span>
                    <span
                      className={`${styles.badge} ${getGiftBadgeClass(gift.state)}`}
                    >
                      {mapGiftStateLabel(gift.state)}
                    </span>
                  </div>
                  <div className={styles.giftPrice}>
                    {formatMoney(gift.price)}
                  </div>
                  <div className={styles.giftFooter}>
                    <span className={styles.giftRemainingLine}>
                      남은수량{' '}
                      <strong>
                        {Number(gift.countRemained || 0).toLocaleString()}
                      </strong>
                      개
                    </span>
                    <div className={styles.giftSaleRow}>
                      <span>
                        판매금액{' '}
                        <strong>
                          {Number(gift.totalSaleAmount || 0).toLocaleString()}
                        </strong>
                        원
                      </span>
                      <button
                        type="button"
                        className={styles.giftEditBtn}
                        disabled={!isGiftEditable(gift.state) || gift.channelInternalYn === 'Y'}
                        title={
                          gift.channelInternalYn === 'Y'
                            ? '채널에서 발행된 기프트는 이 화면에서 수정할 수 없습니다'
                            : undefined
                        }
                        onClick={e => {
                          e.stopPropagation();
                          openEditModal(gift);
                        }}
                      >
                        수정
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {giftState.length === 0 && (
              <div className={styles.empty}>데이터가 없습니다</div>
            )}
          </div>
        </section>
      )}

      {/* 판매현황: 전체/개별 필터 */}
      {activeTab === 'sales' && (
        <section className={styles.section}>
          <div className={styles.filterTabs}>
            <button
              type="button"
              className={`${styles.filterBtn} ${salesFilter === 'all' ? styles.filterActive : ''}`}
              onClick={() => setSalesFilter('all')}
            >
              전체
            </button>
            <button
              type="button"
              className={`${styles.filterBtn} ${salesFilter === 'individual' ? styles.filterActive : ''}`}
              onClick={() => setSalesFilter('individual')}
            >
              개별
            </button>
            <span className={styles.filterDivider} />
            {[['C2', '사용일순'], ['C1', '결제일순'], ['C3', '입금일순']].map(([code, label]) => (
              <button
                key={code}
                type="button"
                className={`${styles.filterBtn} ${styles.filterBtnSort} ${salesSort === code ? styles.filterActive : ''}`}
                onClick={() => handleSalesSort(code)}
              >
                {label}
              </button>
            ))}
          </div>

          {/* 전체: 기존 건별 리스트 */}
          {salesFilter === 'all' && salesRows.length === 0 && (
            <div className={styles.empty}>판매 내역이 없습니다</div>
          )}
          {salesFilter === 'all' && salesRows.map((r, idx) => (
            <div
              className={`${styles.detailCard} ${r.refundState ? styles.detailCardRefunded : ''}`}
              key={idx}
            >
              <div className={styles.detailTop}>
                <span className={`${styles.detailBadge} ${styles.detailSerialBadge}`}>
                  {formatGiftSerialDisplay(r)}
                </span>
              </div>
              <div className={styles.detailRow}>
                <span>선물한사람</span>
                <strong>{formatPerson(r.gifterName, r.gifterEmail)}</strong>
              </div>
              <div className={styles.detailRow}>
                <span>받은사람</span>
                <strong>{formatPerson(r.gifteeName, r.gifteeEmail)}</strong>
              </div>
              <div className={styles.detailRow}>
                <span>결제금액</span>
                <strong>{formatMoney(r.price)}</strong>
              </div>
              {/* [2026-11-05] 쿠폰 사용 — 쓴 쿠폰 이름 · 할인액 · 부담(백엔드 반영 전엔 값이 없어 안 보임) */}
              {couponLinesOf(r).length > 0 && (
                <div className={styles.detailRow}>
                  <span>쿠폰사용</span>
                  <strong>
                    {couponLinesOf(r).map(c => (
                      <div key={c.key}>
                        {c.name} -{Number(c.amount).toLocaleString()}원{c.burden ? ` (${c.burden})` : ''}
                      </div>
                    ))}
                  </strong>
                </div>
              )}
              <div className={styles.detailRow}>
                <span>결제수단</span>
                <strong>
                  <span className={`${styles.valueBadge} ${getPayMethodBadgeClass(r.payMethodDesc)}`}>
                    {r.payMethodDesc || '-'}
                  </span>
                </strong>
              </div>
              <div className={styles.detailRow}>
                <span>결제일시</span>
                <strong>{formatDateTime(r.paymentDttm)}</strong>
              </div>
              <div className={styles.detailRow}>
                <span>사용정보</span>
                <strong>
                  <span className={`${styles.valueBadge} ${getUsageBadgeClass(r.usageState)}`}>
                    {mapUsageLabel(r.usageStateDesc, r.usageState)}
                  </span>
                </strong>
              </div>
              <div className={styles.detailRow}>
                <span>사용일시</span>
                <strong>{formatDateTime(r.usageDttm)}</strong>
              </div>
              <div className={styles.detailRow}>
                <span>입금정보</span>
                <strong>
                  {r.settlementStateDesc && (
                    <span className={`${styles.valueBadge} ${getSettlementBadgeClass(r.settlementStateDesc)}`}>
                      {r.settlementStateDesc}
                    </span>
                  )}
                </strong>
              </div>
              <div className={styles.detailRow}>
                <span>입금일시</span>
                <strong>{formatDateTime(r.depositDttm)}</strong>
              </div>
              {['GS002', 'GS003'].includes(r.usageState) && r.userGiftNo && canCancelUsage(r) && (
                <div style={{ marginTop: 8 }}>
                  <button
                    type="button"
                    className={styles.cancelBtn}
                    onClick={() => handleUsageCancel(r.userGiftNo, idx)}
                  >
                    사용취소
                  </button>
                </div>
              )}
              {renderOwnerRefundSection(r, idx, 'all')}
            </div>
          ))}

          {/* 개별: 기프트 목록 → 클릭 시 해당 기프트 판매현황 모달 */}
          {salesFilter === 'individual' && (
            <div className={styles.card}>
              {giftState.map(gift => (
                <div
                  className={`${styles.giftItem} ${styles.barItemClickable}`}
                  key={gift.giftNo}
                  onClick={() => handleGiftClick(gift)}
                >
                  <img
                    className={styles.giftImg}
                    src={gift.thumbnailFileUrl || ''}
                    alt=""
                  />
                  <div className={styles.giftContent}>
                    <div className={styles.giftHeader}>
                      <span className={styles.giftName}>
                        {formatGiftSerialDisplay(gift)}
                      </span>
                      <span className={`${styles.badge} ${getGiftBadgeClass(gift.state)}`}>
                        {mapGiftStateLabel(gift.state)}
                      </span>
                    </div>
                    <div className={styles.giftPrice}>{formatMoney(gift.price)}</div>
                    <div className={styles.giftStats}>
                      <span>남은수량 <strong>{Number(gift.countRemained || 0).toLocaleString()}</strong>개</span>
                      <span>판매금액 <strong>{Number(gift.totalSaleAmount || 0).toLocaleString()}</strong>원</span>
                      <span className={styles.giftChevron}>›</span>
                    </div>
                  </div>
                </div>
              ))}
              {giftState.length === 0 && (
                <div className={styles.empty}>데이터가 없습니다</div>
              )}
            </div>
          )}
        </section>
      )}

      {/* 직원 관리 */}
      {activeTab === 'staff' && (
        <section className={styles.section}>
          {/* 검색은 버튼·엔터로만 실행한다(입력 중 조회 없음). */}
          <div className={styles.staffSearchRow}>
            <input
              className={styles.searchBox}
              placeholder="이름·아이디 검색"
              value={staffSearchInput}
              onChange={e => setStaffSearchInput(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  runStaffSearch();
                }
              }}
            />
            <button
              type="button"
              className={`${styles.staffInlineBtn} ${styles.staffSearchBtn}`}
              onClick={runStaffSearch}
              disabled={staffLoading}
            >
              검색
            </button>
          </div>
          <div className={styles.staffAddRow}>
            <span className={styles.staffAddNotice}>
              SMS 비용 부담으로 직원등록은 1명만 가능합니다.
            </span>
            <button
              type="button"
              className={styles.staffAddBtn}
              onClick={openStaffCreate}
              disabled={staffTotal >= 1}
            >
              직원 등록
            </button>
          </div>
          <div className={styles.card}>
            {staffLoading ? (
              <div className={styles.staffEmpty}>불러오는 중...</div>
            ) : staffList.length === 0 ? (
              <div className={styles.staffEmpty}>등록된 직원이 없습니다.</div>
            ) : (
              staffList.map(row => (
                <div
                  className={`${styles.staffItem} ${styles.staffItemTap}`}
                  key={row.adminNo}
                  onClick={() => openStaffEdit(row)}
                >
                  <div className={styles.staffTop}>
                    <span className={styles.staffName}>
                      {row.adminName || '-'}
                    </span>
                    <span className={styles.staffState}>
                      {row.state === 'U' ? '사용중' : row.state}
                    </span>
                  </div>
                  <div className={styles.staffSub}>
                    {row.adminId}
                    {row.mobile ? ` · ${row.mobile}` : ''}
                    {row.regDttm
                      ? ` · ${String(row.regDttm).slice(0, 10)}`
                      : ''}
                  </div>
                </div>
              ))
            )}
          </div>
        </section>
      )}

      {/* 직원 등록·수정 모달. 기프트 수정 모달의 구조·클래스를 그대로 따른다. */}
      {staffModalOpen && (
        <div
          className={styles.modalOverlay}
          onClick={() => setStaffModalOpen(false)}
        >
          <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <span className={styles.modalTitle}>
                {staffEditingId ? '직원 수정' : '직원 등록'}
              </span>
              <button
                type="button"
                className={styles.modalClose}
                onClick={() => setStaffModalOpen(false)}
              >
                ×
              </button>
            </div>
            <div className={styles.modalBody} ref={staffModalBodyRef}>
              {staffError && (
                <div className={styles.editError}>{staffError}</div>
              )}
              <div className={styles.editFormRow}>
                <label className={styles.editLabel}>
                  아이디 <span className={styles.required}>*</span>
                </label>
                <div className={styles.staffInlineRow}>
                  <input
                    className={styles.editInput}
                    type="text"
                    value={staffForm.adminId}
                    disabled={!!staffEditingId}
                    onChange={e => {
                      setStaffForm(f => ({ ...f, adminId: e.target.value }));
                      setStaffIdCheckPassed(false);
                      // SMS 인증은 (adminId, mobile) 조합으로 서버에 저장되므로
                      //  아이디가 바뀌면 이전 인증은 이 새 아이디에 대해선 무효다.
                      //  (staffManage.js:290-293 와 같은 규칙)
                      setStaffVerifiedMobile('');
                    }}
                    placeholder="아이디"
                  />
                  <button
                    type="button"
                    className={styles.staffInlineBtn}
                    onClick={checkStaffDuplicateId}
                    disabled={!!staffEditingId}
                  >
                    중복확인
                  </button>
                </div>
                {!staffEditingId && (
                  <div
                    className={`${styles.staffIdStatus} ${
                      staffIdCheckPassed &&
                      staffIdCheckedValue === staffForm.adminId.trim()
                        ? styles.staffIdStatusOk
                        : styles.staffIdStatusNg
                    }`}
                  >
                    {staffIdCheckPassed &&
                    staffIdCheckedValue === staffForm.adminId.trim()
                      ? '중복확인 완료'
                      : '중복확인이 필요합니다'}
                  </div>
                )}
              </div>
              <div className={styles.editFormRow}>
                <label className={styles.editLabel}>
                  비밀번호
                  {!staffEditingId && (
                    <span className={styles.required}> *</span>
                  )}
                </label>
                <div className={styles.staffPwWrap}>
                  <input
                    className={`${styles.editInput} ${styles.staffPwInput}`}
                    type={showStaffPassword ? 'text' : 'password'}
                    value={staffForm.password}
                    onChange={e =>
                      setStaffForm(f => ({ ...f, password: e.target.value }))
                    }
                    placeholder={
                      staffEditingId ? '변경할 때만 입력 (8자 이상)' : '8자 이상'
                    }
                  />
                  <button
                    type="button"
                    className={styles.staffPwToggle}
                    onClick={() => setShowStaffPassword(v => !v)}
                    aria-label={
                      showStaffPassword ? '비밀번호 숨기기' : '비밀번호 보기'
                    }
                  >
                    {showStaffPassword ? (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" /><line x1="1" y1="1" x2="23" y2="23" /></svg>
                    ) : (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8Z" /><circle cx="12" cy="12" r="3" /></svg>
                    )}
                  </button>
                </div>
              </div>
              <div className={styles.editFormRow}>
                <label className={styles.editLabel}>
                  비밀번호 확인
                  {!staffEditingId && (
                    <span className={styles.required}> *</span>
                  )}
                </label>
                <div className={styles.staffPwWrap}>
                  <input
                    className={`${styles.editInput} ${styles.staffPwInput}`}
                    type={showStaffPasswordConfirm ? 'text' : 'password'}
                    value={staffForm.passwordConfirm}
                    onChange={e =>
                      setStaffForm(f => ({
                        ...f,
                        passwordConfirm: e.target.value,
                      }))
                    }
                    placeholder={
                      staffEditingId ? '변경할 때만 입력 (8자 이상)' : '8자 이상'
                    }
                  />
                  <button
                    type="button"
                    className={styles.staffPwToggle}
                    onClick={() => setShowStaffPasswordConfirm(v => !v)}
                    aria-label={
                      showStaffPasswordConfirm
                        ? '비밀번호 숨기기'
                        : '비밀번호 보기'
                    }
                  >
                    {showStaffPasswordConfirm ? (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" /><line x1="1" y1="1" x2="23" y2="23" /></svg>
                    ) : (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8Z" /><circle cx="12" cy="12" r="3" /></svg>
                    )}
                  </button>
                </div>
                {staffPasswordTouched && (
                  <div
                    className={`${styles.staffIdStatus} ${
                      staffPasswordMatched
                        ? styles.staffIdStatusOk
                        : styles.staffIdStatusNg
                    }`}
                  >
                    {staffPasswordMatched
                      ? '비밀번호가 일치합니다'
                      : '비밀번호가 일치하지 않습니다'}
                  </div>
                )}
              </div>
              <div className={styles.editFormRow}>
                <label className={styles.editLabel}>
                  이름 <span className={styles.required}>*</span>
                </label>
                <input
                  className={styles.editInput}
                  type="text"
                  value={staffForm.adminName}
                  onChange={e =>
                    setStaffForm(f => ({ ...f, adminName: e.target.value }))
                  }
                  placeholder="이름"
                />
              </div>
              <div className={styles.editFormRow}>
                <label className={styles.editLabel}>
                  휴대폰
                  {!staffEditingId && (
                    <span className={styles.required}> *</span>
                  )}
                </label>
                <div className={styles.staffInlineRow}>
                  <input
                    className={styles.editInput}
                    type="tel"
                    value={staffForm.mobile}
                    onChange={e =>
                      setStaffForm(f => ({
                        ...f,
                        mobile: e.target.value.replace(/\D/g, ''),
                      }))
                    }
                    placeholder="숫자만 입력"
                  />
                  {/* 수정 모드에서도 노출한다 — 번호를 바꾸면 재인증이 필요하다.
                      호출 API 는 staffSmsUrls 가 모드별로 가른다. */}
                  <button
                    type="button"
                    className={styles.staffInlineBtn}
                    onClick={() => setStaffSmsModalOpen(true)}
                  >
                    인증
                  </button>
                </div>
                {/* 인증 완료 표시도 모드 무관으로 노출한다. 수정 모드에서 번호를 그대로 두면
                    openStaffEdit 이 넣어 둔 값과 같아 '완료'로 보인다.
                    ★번호 입력이 비어 있으면 블록 자체를 렌더하지 않는다 — 기존 번호가 없는 직원을
                    수정할 때 인증할 대상이 없는데 '인증이 필요합니다'가 띄는 것을 막는다. */}
                {staffForm.mobile.trim() && (
                  <div
                    className={`${styles.staffIdStatus} ${
                      staffForm.mobile.trim() &&
                      staffForm.mobile.trim() === staffVerifiedMobile
                        ? styles.staffIdStatusOk
                        : styles.staffIdStatusNg
                    }`}
                  >
                    {staffForm.mobile.trim() &&
                    staffForm.mobile.trim() === staffVerifiedMobile
                      ? '휴대폰 인증 완료'
                      : '휴대폰 인증이 필요합니다'}
                  </div>
                )}
              </div>
              <button
                type="button"
                className={styles.editSubmitBtn}
                onClick={saveStaff}
                disabled={staffBusy}
              >
                {staffBusy ? '저장 중...' : '저장'}
              </button>
              {staffEditingId && (
                <button
                  type="button"
                  className={styles.staffDeleteBtn}
                  onClick={deleteStaff}
                  disabled={staffBusy}
                >
                  직원 삭제
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* SMS 본인확인 모달. staffManage.js:233-240 과 같은 연결. */}
      <SmsVerifyModal
        open={staffSmsModalOpen}
        onClose={() => setStaffSmsModalOpen(false)}
        maskedPhone={staffForm.mobile}
        onSend={handleStaffSmsSend}
        onVerify={handleStaffSmsVerify}
        t={smsT}
      />
      {/* 기프트별 판매현황 모달 */}
      {selectedGift && (
        <div
          className={styles.modalOverlay}
          onClick={() => setSelectedGift(null)}
        >
          <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <span className={styles.modalTitle}>
                {selectedGift.giftName} 판매현황
              </span>
              <button
                type="button"
                className={styles.modalClose}
                onClick={() => setSelectedGift(null)}
              >
                ×
              </button>
            </div>
            <div className={styles.modalBody} ref={modalBodyRef}>
              {giftSalesLoading && (
                <div className={styles.empty}>불러오는 중...</div>
              )}
              {!giftSalesLoading && giftSalesRows.length === 0 && (
                <div className={styles.empty}>판매 내역이 없습니다</div>
              )}
              {giftSalesRows.map((r, idx) => (
                <div
                  className={`${styles.detailCard} ${r.refundState ? styles.detailCardRefunded : ''}`}
                  key={idx}
                >
                  <div className={styles.detailTop}>
                    <span className={`${styles.detailBadge} ${styles.detailSerialBadge}`}>
                      {formatGiftSerialDisplay(r)}
                    </span>
                  </div>
                  <div className={styles.detailRow}>
                    <span>선물한사람</span>
                    <strong>{formatPerson(r.gifterName, r.gifterEmail)}</strong>
                  </div>
                  <div className={styles.detailRow}>
                    <span>받은사람</span>
                    <strong>{formatPerson(r.gifteeName, r.gifteeEmail)}</strong>
                  </div>
                  <div className={styles.detailRow}>
                    <span>결제금액</span>
                    <strong>{formatMoney(r.price)}</strong>
                  </div>
                  {/* [2026-11-05] 쿠폰 사용 — 쓴 쿠폰 이름 · 할인액 · 부담(백엔드 반영 전엔 값이 없어 안 보임) */}
                  {couponLinesOf(r).length > 0 && (
                    <div className={styles.detailRow}>
                      <span>쿠폰사용</span>
                      <strong>
                        {couponLinesOf(r).map(c => (
                          <div key={c.key}>
                            {c.name} -{Number(c.amount).toLocaleString()}원{c.burden ? ` (${c.burden})` : ''}
                          </div>
                        ))}
                      </strong>
                    </div>
                  )}
                  <div className={styles.detailRow}>
                    <span>결제수단</span>
                    <strong>
                      <span className={`${styles.valueBadge} ${getPayMethodBadgeClass(r.payMethodDesc)}`}>
                        {r.payMethodDesc || '-'}
                      </span>
                    </strong>
                  </div>
                  <div className={styles.detailRow}>
                    <span>결제일시</span>
                    <strong>{formatDateTime(r.paymentDttm)}</strong>
                  </div>
                  <div className={styles.detailRow}>
                    <span>사용정보</span>
                    <strong>
                      <span className={`${styles.valueBadge} ${getUsageBadgeClass(r.usageState)}`}>
                        {mapUsageLabel(r.usageStateDesc, r.usageState)}
                      </span>
                    </strong>
                  </div>
                  <div className={styles.detailRow}>
                    <span>사용일시</span>
                    <strong>{formatDateTime(r.usageDttm)}</strong>
                  </div>
                  <div className={styles.detailRow}>
                    <span>입금정보</span>
                    <strong>
                      {r.settlementStateDesc && (
                        <span className={`${styles.valueBadge} ${getSettlementBadgeClass(r.settlementStateDesc)}`}>
                          {r.settlementStateDesc}
                        </span>
                      )}
                    </strong>
                  </div>
                  <div className={styles.detailRow}>
                    <span>입금일시</span>
                    <strong>{formatDateTime(r.depositDttm)}</strong>
                  </div>
                  {['GS002', 'GS003'].includes(r.usageState) && r.userGiftNo && canCancelUsage(r) && (
                    <div style={{ marginTop: 8 }}>
                      <button
                        type="button"
                        className={styles.cancelBtn}
                        onClick={() => handleGiftSalesUsageCancel(r.userGiftNo, idx)}
                      >
                        사용취소
                      </button>
                    </div>
                  )}
                  {renderOwnerRefundSection(r, idx, 'gift')}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      {/* 기프트 수정 모달 */}
      {editGift && (
        <div className={styles.modalOverlay} onClick={() => setEditGift(null)}>
          <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <span className={styles.modalTitle}>{editForm.isNew ? wt.registerGift : '기프트 수정'}</span>
              <button type="button" className={styles.modalClose} onClick={() => setEditGift(null)}>×</button>
            </div>
            <div className={styles.modalBody} ref={modalBodyRef}>
              {editError && <div className={styles.editError}>{editError}</div>}
              {!editForm.isNew && (
              <div className={styles.editStateRow}>
                <span className={`${styles.badge} ${getGiftBadgeClass(editForm.state)}`}>
                  {mapGiftStateLabel(editForm.state)}
                </span>
                {(editForm.state === 'SS001' || editForm.state === 'SS002' || editForm.state === 'SS003') && (
                  <button
                    type="button"
                    className={editForm.state === 'SS002' ? styles.stateStopBtn : styles.stateStartBtn}
                    onClick={() => handleGiftStateChange(editForm.giftNo, editForm.state)}
                  >
                    {editForm.state === 'SS001' ? '판매 시작' : editForm.state === 'SS002' ? '판매 중지' : '판매 재개'}
                  </button>
                )}
              </div>
              )}
              {/* 이미지 */}
              <div className={styles.editFormRow}>
                <label className={styles.editLabel}>기프트 이미지 <span className={styles.required}>*</span></label>
                <div className={styles.editImageWrap}>
                  {editImageFile?.preview && (
                    <img src={editImageFile.preview} alt="기프트 이미지" className={styles.editImagePreview} />
                  )}
                  {isGiftEditable(editForm.state) && (
                    <label className={styles.editImageBtn}>
                      <input
                        type="file"
                        accept=".jpg,.jpeg,.gif,.png"
                        style={{ display: 'none' }}
                        onChange={async e => {
                          const input = e.target;
                          const picked = input.files?.[0];
                          if (!picked) return;
                          // 웹과 같이 큰 사진은 긴 변 1,500px로 자동 축소 — 줄여도 3MB를 넘을 때(큰 GIF 등)만 안내
                          const f = await prepareGiftImage(picked);
                          if (!f) {
                            input.value = null;
                            failEdit(wt.imageSizeError);
                            return;
                          }
                          const reader = new FileReader();
                          reader.onload = ev => setEditImageFile({ preview: ev.target.result, file: f });
                          reader.readAsDataURL(f);
                        }}
                      />
                      {editImageFile?.preview ? '이미지 변경' : '이미지 선택'}
                    </label>
                  )}
                  {isGiftEditable(editForm.state) && (
                    <div style={{ fontSize: 12, color: '#888' }}>{wt.giftUploadFileNote}</div>
                  )}
                </div>
              </div>
              <div className={styles.editFormRow}>
                <label className={styles.editLabel}>카테고리</label>
                <select
                  className={styles.editSelect}
                  value={editForm.categoryCode || ''}
                  disabled={!isGiftEditable(editForm.state)}
                  onChange={e => setEditForm(f => ({ ...f, categoryCode: e.target.value }))}
                >
                  {categoryData.length === 0 && (
                    <option value={editForm.categoryCode || ''}>{editForm.categoryCode || '로딩 중...'}</option>
                  )}
                  {categoryData.map(c => (
                    <option key={c.detailCode} value={c.detailCode}>
                      {t.selectCategory?.[c.detailCodeName] || c.detailCodeName}
                    </option>
                  ))}
                </select>
              </div>
              <div className={styles.editFormRow}>
                <label className={styles.editLabel}>기프트명 <span className={styles.required}>*</span></label>
                <input
                  className={styles.editInput}
                  type="text"
                  maxLength={50}
                  value={editForm.giftName}
                  disabled={!isGiftEditable(editForm.state)}
                  onChange={e => setEditForm(f => ({ ...f, giftName: e.target.value }))}
                  placeholder="기프트명"
                />
                {(editForm.giftName || '').length >= 50 && (
                  <div style={{ color: '#e53e3e', fontSize: 12, marginTop: 4 }}>{wt.giftNameMaxChars}</div>
                )}
              </div>
              <div className={styles.editFormRow}>
                <label className={styles.editLabel}>기프트 설명{editForm.isNew && <> <span className={styles.required}>*</span></>}</label>
                <textarea
                  className={styles.editTextarea}
                  maxLength={100}
                  value={editForm.giftDesc}
                  disabled={!isGiftEditable(editForm.state)}
                  onChange={e => setEditForm(f => ({ ...f, giftDesc: e.target.value }))}
                  placeholder="기프트 설명"
                  rows={3}
                />
                {(editForm.giftDesc || '').length >= 100 && (
                  <div style={{ color: '#e53e3e', fontSize: 12, marginTop: 4 }}>{wt.giftAdMaxChars}</div>
                )}
              </div>
              <div className={styles.editFormRow}>
                <label className={styles.editLabel}>정상가격 (원) <span className={styles.required}>*</span></label>
                <input
                  className={styles.editInput}
                  type="number"
                  min={0}
                  value={editForm.basePrice}
                  disabled={!isGiftEditable(editForm.state)}
                  onChange={e => setEditForm(f => ({ ...f, basePrice: e.target.value }))}
                  placeholder="0"
                  step={100}
                />
                {priceInputError(editForm.basePrice, wt, 1000000, editForm.isNew) && (
                  <div style={{ color: '#e53e3e', fontSize: 12, marginTop: 4 }}>{priceInputError(editForm.basePrice, wt, 1000000, editForm.isNew)}</div>
                )}
              </div>
              <div className={styles.editFormRow}>
                <label className={styles.editLabel}>할인가격 (원)</label>
                <input
                  className={styles.editInput}
                  type="number"
                  min={0}
                  value={editCanDiscount || !isGiftEditable(editForm.state) ? editForm.discountPrice : ''}
                  disabled={!isGiftEditable(editForm.state) || !editCanDiscount}
                  onChange={e => setEditForm(f => ({ ...f, discountPrice: e.target.value }))}
                  placeholder={editCanDiscount || !isGiftEditable(editForm.state) ? '없으면 비워두세요' : '바우처 프로모션 선택 시 입력'}
                  step={100}
                />
                {editCanDiscount && priceInputError(editForm.discountPrice, wt) && (
                  <div style={{ color: '#e53e3e', fontSize: 12, marginTop: 4 }}>{priceInputError(editForm.discountPrice, wt)}</div>
                )}
                {editDiscountDropped && (
                  <div style={{ color: '#d93025', fontSize: 12, marginTop: 4 }}>
                    바우처 프로모션이 연결돼 있지 않아, 저장하면 기존 할인({editLoadedDiscountNum.toLocaleString()}원)이 해제되고 판매가가 정상가격으로 바뀝니다.
                  </div>
                )}
              </div>
              {(() => {
                const bpNum = parseInt(editForm.basePrice, 10) || 0;
                const dpNum = editDiscountNum;
                const salePrice = dpNum > 0 ? bpNum - dpNum : bpNum;
                return (
                  <div className={styles.editFormRow}>
                    <label className={styles.editLabel}>판매가 (최종 결제금액)</label>
                    <input
                      className={styles.editInput}
                      type="text"
                      disabled
                      value={salePrice > 0 ? `${salePrice.toLocaleString()}원` : ''}
                    />
                    {/* 웹(giftManage.js)과 같이 판매가 5만 원 이상이면 인지세 안내 */}
                    {salePrice >= 50000 && (
                      <div style={{ marginTop: 8, fontSize: 12, lineHeight: 1.45, color: '#92400e', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 8, padding: '8px 10px' }}>
                        {wt.giftStampDutyNotice}
                      </div>
                    )}
                  </div>
                );
              })()}
              {/* [2026-10-28] 바우처 프로모션 선택 — 웹 기프트 등록 · 수정과 같은 칸(단일 선택) */}
              <div className={styles.editFormRow}>
                <label className={styles.editLabel}>바우처 프로모션 선택</label>
                <select
                  className={styles.editInput}
                  value={editForm.discountPromotionNo || ''}
                  disabled={!isGiftEditable(editForm.state)}
                  onChange={e =>
                    // [2026-10-30] 프로모션을 빼면 할인가격도 비운다(할인은 바우처 프로모션 적용 시에만)
                    setEditForm(f => ({
                      ...f,
                      discountPromotionNo: e.target.value,
                      ...(!e.target.value ? { discountPrice: '' } : {}),
                    }))
                  }
                >
                  <option value="">
                    {availableVouchers.length ? '선택 안 함' : '선택 가능한 바우처 프로모션 없음'}
                  </option>
                  {availableVouchers.map(v => (
                    <option key={v.promotionNo} value={String(v.promotionNo)}>
                      [{ISSUER_LABEL[v.issuerType] || v.issuerType}] {v.name} (잔여{' '}
                      {((v.totalAmount ?? 0) - (v.usedAmount ?? 0)).toLocaleString()}원)
                    </option>
                  ))}
                </select>
                <div style={{ marginTop: 4, fontSize: 12, color: '#888' }}>
                  선택하면 할인가격만큼 이 프로모션 예산에서 차감됩니다
                </div>
                {vouchersLoaded &&
                  !!editForm.discountPromotionNo &&
                  !availableVouchers.some(v => String(v.promotionNo) === String(editForm.discountPromotionNo)) && (
                    <div style={{ marginTop: 4, fontSize: 12, color: '#d93025' }}>
                      연결돼 있던 프로모션(#{editForm.discountPromotionNo})은 기간이 끝났거나 사용할 수 없어, 저장하면 연결이 해제됩니다.
                    </div>
                  )}
              </div>
              {/* [2026-10-28] 쿠폰 적용 허용 + 쿠폰 선택 — 웹과 같은 부품(GiftCouponPicker) */}
              <div className={styles.editFormRow}>
                <label className={styles.editLabel}>쿠폰 적용 허용</label>
                <select
                  className={styles.editInput}
                  value={editForm.couponEligibleYn || 'Y'}
                  disabled={!isGiftEditable(editForm.state)}
                  onChange={e => {
                    const value = e.target.value;
                    setEditForm(f => ({ ...f, couponEligibleYn: value }));
                    // 불가로 바꾸면 선택값을 비운다
                    if (value !== 'Y') setSelectedCouponNos([]);
                    setCouponSelectError('');
                  }}
                >
                  <option value="Y">허용 (Y)</option>
                  <option value="N">불가 (N)</option>
                </select>
                <div style={{ marginTop: 4, fontSize: 12, color: '#888' }}>
                  구매자가 결제 시 프로모션 쿠폰을 쓸 수 있는지
                </div>
                {(editForm.couponEligibleYn || 'Y') === 'Y' && (
                  <GiftCouponPicker
                    candidates={couponCandidates}
                    selected={selectedCouponNos}
                    endedSelected={endedCoupons.filter(
                      c => !couponCandidates.some(x => Number(x.promotionNo) === Number(c.promotionNo)),
                    )}
                    loaded={couponsLoaded}
                    disabled={!isGiftEditable(editForm.state)}
                    error={couponSelectError}
                    onChange={next => {
                      setSelectedCouponNos(next);
                      if (next.length) setCouponSelectError('');
                    }}
                  />
                )}
              </div>
              <div className={styles.editFormRow}>
                <label className={styles.editLabel}>판매 시작일 <span className={styles.required}>*</span></label>
                <input
                  className={styles.editInput}
                  type="date"
                  value={editForm.saleStartDttm}
                  min={new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Seoul' })}
                  disabled={!isGiftEditable(editForm.state)}
                  onChange={e => setEditForm(f => ({
                    ...f,
                    saleStartDttm: e.target.value,
                    saleEndDttm: f.saleEndDttm && f.saleEndDttm < e.target.value ? e.target.value : f.saleEndDttm,
                    saleEndPre: f.saleEndDttm && f.saleEndDttm < e.target.value ? e.target.value : f.saleEndPre,
                  }))}
                />
              </div>
              <div className={styles.editFormRow}>
                <label className={styles.editLabel}>판매 종료일 <span className={styles.required}>*</span></label>
                <input
                  className={styles.editInput}
                  type="date"
                  value={editForm.saleEndUnlimited ? '' : editForm.saleEndDttm}
                  min={editForm.saleStartDttm || new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Seoul' })}
                  disabled={!isGiftEditable(editForm.state) || editForm.saleEndUnlimited}
                  onChange={e => setEditForm(f => ({ ...f, saleEndDttm: e.target.value, saleEndPre: e.target.value }))}
                />
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 6, fontSize: 13 }}>
                  <input
                    type="checkbox"
                    checked={!!editForm.saleEndUnlimited}
                    disabled={!isGiftEditable(editForm.state)}
                    onChange={e => {
                      const checked = e.target.checked;
                      // 웹과 같이 무기한 체크 시 종료일을 비우고, 해제하면 직전에 고른 날짜를 되살린다
                      setEditForm(f => ({
                        ...f,
                        saleEndUnlimited: checked,
                        saleEndDttm: checked ? '' : (f.saleEndPre && f.saleEndPre >= f.saleStartDttm ? f.saleEndPre : ''),
                      }));
                    }}
                  />
                  {wt.giftUnlimited}
                </label>
              </div>
              <div className={styles.editFormRow}>
                <label className={styles.editLabel}>총발행수량 <span className={styles.required}>*</span></label>
                <input
                  className={styles.editInput}
                  type="number"
                  min={1}
                  value={editForm.countUnlimited ? '' : editForm.countIssued}
                  disabled={!isGiftEditable(editForm.state) || editForm.countUnlimited}
                  onChange={e => setEditForm(f => ({ ...f, countIssued: e.target.value }))}
                  placeholder={editForm.countUnlimited ? '무제한' : '수량 입력'}
                />
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 6, fontSize: 13 }}>
                  <input
                    type="checkbox"
                    checked={!!editForm.countUnlimited}
                    disabled={!isGiftEditable(editForm.state)}
                    onChange={e => setEditForm(f => ({ ...f, countUnlimited: e.target.checked }))}
                  />
                  {wt.unlimited}
                </label>
              </div>
              {isGiftEditable(editForm.state) && (
              <button
                type="button"
                className={styles.editSubmitBtn}
                onClick={saveGiftEdit}
                disabled={editBusy}
              >
                {editForm.isNew
                  ? (editBusy ? '등록 중...' : wt.register)
                  : (editBusy ? '저장 중...' : '저장')}
              </button>
              )}
              {/* 웹(giftManage.js)과 같이 판매예정·판매중·판매중지 기프트만 판매취소 가능 */}
              {!editForm.isNew && ['SS001', 'SS002', 'SS003'].includes(editForm.state) && (
              <button
                type="button"
                className={styles.editCancelSaleBtn}
                onClick={cancelGiftSale}
                disabled={editBusy}
              >
                {wt.giftCancelSale}
              </button>
              )}
              <button
                type="button"
                className={styles.editBackBtn}
                onClick={() => setEditGift(null)}
                disabled={editBusy}
              >
                이전
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
